// canvas/canvasThermo.js
// Thermodynamic calculation engine for the process canvas.
/* eslint-disable no-unused-vars */

import { COMPONENTS, R, pengRobinsonZ } from '../../fluid-properties';
import { mixtureEnthalpyDeparture_Jmol as penelouxDeparture } from './penelouxEnthalpy';

export { R };

// ─── Cubic equation solver (Cardano) ─────────────────────────────────────────
function solveCubic(a, b, c, d) {
  if (Math.abs(a) < 1e-12) {
    if (Math.abs(b) < 1e-12) return [ -d / c ];
    const disc = c * c - 4 * b * d;
    if (disc < 0) return [ -c / (2 * b) ];
    return [ (-c - Math.sqrt(disc)) / (2 * b), (-c + Math.sqrt(disc)) / (2 * b) ].sort((x, y) => x - y);
  }
  const p = (3 * a * c - b * b) / (3 * a * a);
  const q = (2 * b * b * b - 9 * a * b * c + 27 * a * a * d) / (27 * a * a * a);
  const disc = q * q / 4 + p * p * p / 27;
  let roots = [];
  if (disc > 0) {
    const u = Math.cbrt(-q / 2 + Math.sqrt(disc));
    const v = Math.cbrt(-q / 2 - Math.sqrt(disc));
    roots = [ u + v - b / (3 * a) ];
  } else if (Math.abs(disc) < 1e-18) {
    const u = Math.cbrt(-q / 2);
    roots = [ 2 * u - b / (3 * a), -u - b / (3 * a) ];
  } else {
    const r = Math.sqrt(-4 * p / 3);
    const phi = Math.acos(Math.max(-1, Math.min(1, -q / Math.sqrt(-4 * p ** 3 / 27))));
    roots = [
      r * Math.cos(phi / 3) - b / (3 * a),
      r * Math.cos((phi + 2 * Math.PI) / 3) - b / (3 * a),
      r * Math.cos((phi + 4 * Math.PI) / 3) - b / (3 * a)
    ];
  }
  return roots.sort((x, y) => x - y);
}

// ─── Component registry ──────────────────────────────────────────────────────
export const COMP_LIST = [
  COMPONENTS.METHANE, COMPONENTS.ETHANE, COMPONENTS.PROPANE, COMPONENTS.BUTANE,
  COMPONENTS.HEPTANE, COMPONENTS.OCTANE, COMPONENTS.NONANE,
  COMPONENTS.NITROGEN, COMPONENTS.CO2, COMPONENTS.H2S, COMPONENTS.WATER,
];
// Component indices: CH4=0, C2=1, C3=2, nC4=3, nC7=4, nC8=5, nC9=6, N2=7, CO2=8, H2S=9, H2O=10
// N_COMP = 11
export const N_COMP = COMP_LIST.length;
export const COMP_IDX = {};
COMP_LIST.forEach((c, i) => { COMP_IDX[c.name] = i; });

// ─── Stream helpers ──────────────────────────────────────────────────────────
export function makeStream(id, T_K, P_bar, F_kmols, z_arr) {
  const z = new Float64Array(N_COMP);
  let sum = 0;
  z_arr.forEach((v, i) => { z[i] = v; sum += v; });
  if (sum > 0) z.forEach((_, i) => { z[i] /= sum; });
  return { id, T_K, P_bar, F_kmols, z, phase: 'V', valid: true };
}
export function cloneStream(s, id) { return { ...s, id, z: new Float64Array(s.z) }; }
export function emptyStream(id) {
  return { id, T_K: 288.15, P_bar: 1.01325, F_kmols: 0, z: new Float64Array(N_COMP), phase: 'V', valid: false };
}

export function mixtureMW(z) {
  let MW = 0;
  for (let i = 0; i < N_COMP; i++) MW += z[i] * COMP_LIST[i].MW;
  return MW;
}

export function mixtureZ(T_K, P_bar, z) {
  let Tpc = 0, Ppc = 0, omega = 0;
  for (let i = 0; i < N_COMP; i++) {
    Tpc += z[i] * COMP_LIST[i].Tc;
    Ppc += z[i] * COMP_LIST[i].Pc;
    omega += z[i] * COMP_LIST[i].omega;
  }
  const res = pengRobinsonZ(T_K, P_bar, Tpc, Ppc, omega);
  return { Z: res.Z, Tpc, Ppc, omega };
}

export function mixtureCp_J_molK(T_K, z) {
  let Cp = 0;
  for (let i = 0; i < N_COMP; i++) {
    const [A, B, C, D] = COMP_LIST[i].cp;
    Cp += z[i] * (A + B * T_K + C * T_K ** 2 + D * T_K ** 3);
  }
  return Cp;
}

export function streamEnthalpy_kJh(stream) {
  const T = stream.T_K, T0 = 273.15;
  let H = 0;
  for (let i = 0; i < N_COMP; i++) {
    const [A, B, C, D] = COMP_LIST[i].cp;
    H += stream.z[i] * (A * (T - T0) + B / 2 * (T ** 2 - T0 ** 2) + C / 3 * (T ** 3 - T0 ** 3) + D / 4 * (T ** 4 - T0 ** 4));
  }
  return stream.F_kmols * H;
}

// ─── BIPs ────────────────────────────────────────────────────────────────────
function getKij(i, j, T_K) {
  if (i === j) return 0;
  const [idx1, idx2] = i < j ? [i, j] : [j, i];
  // New indices: CH4=0, C2=1, C3=2, nC4=3, nC7=4, nC8=5, nC9=6, N2=7, CO2=8, H2S=9, H2O=10
  if (idx2 === 10) { // Water
    if (idx1 === 0) return 0.485; if (idx1 === 1) return 0.492;
    if (idx1 >= 2 && idx1 <= 6) return 0.5525; // C3–C9
    if (idx1 === 7) return 0.4778; // N2
    if (idx1 === 8) return 0.1896; // CO2
    if (idx1 === 9) return 0.19031 - 0.05965 * (T_K / 373.2); // H2S
  }
  if (idx1 === 7) { // N2
    if (idx2 === 0) return 0.0311; if (idx2 === 1) return 0.0515;
    if (idx2 === 2) return 0.0852; if (idx2 >= 3 && idx2 <= 6) return 0.100; if (idx2 === 9) return 0.1696;
  }
  if (idx1 === 8) { // CO2
    if (idx2 === 0) return 0.0919; if (idx2 === 1) return 0.1322;
    if (idx2 === 2) return 0.1241; if (idx2 >= 3 && idx2 <= 6) return 0.120; if (idx2 === 9) return 0.0974;
  }
  if (idx1 === 9) { // H2S
    if (idx2 === 0) return 0.0714; if (idx2 === 1) return 0.0837;
    if (idx2 === 2) return 0.0878; if (idx2 >= 3 && idx2 <= 6) return 0.060;
  }
  if (idx1 === 0) { // CH4
    if (idx2 === 2) return 0.007; if (idx2 === 3) return 0.013;
    if (idx2 === 4) return 0.020; if (idx2 === 5) return 0.028; if (idx2 === 6) return 0.032;
  }
  return 0;
}

// ─── Fugacity ────────────────────────────────────────────────────────────────
// ─── Lee-Kesler vapour pressure: reliable Psat from Tc, Pc, omega ────────────
// Pitzer correlation: ln(Psat/Pc) = f0(Tr) + omega * f1(Tr)
// Valid for 0.5 < Tr < 1.0. Returns bar. Returns 0 above Tc or below triple point.
function leeKeslerPsat(comp, T_K) {
  const Tr = T_K / comp.Tc;
  if (Tr >= 1.0) return comp.Pc; // at/above critical — use Pc as ceiling
  if (Tr <= 0.15) return 1e-10;  // too cold — essentially zero pressure
  const f0 = 5.92714 - 6.09648 / Tr - 1.28862 * Math.log(Tr) + 0.169347 * Tr ** 6;
  const f1 = 15.2518 - 15.6875 / Tr - 13.4721 * Math.log(Tr) + 0.43577 * Tr ** 6;
  const lnPr = f0 + comp.omega * f1;
  return comp.Pc * Math.exp(Math.max(-50, Math.min(50, lnPr)));
}

// K-value from modified Raoult's Law using Lee-Kesler Psat
// K_i = Psat_i(T) / P  (assumes ideal vapour, ideal liquid — good for hydrocarbons)
// For light gases above Tc (CH4, N2, CO2 at typical process T), use Wilson K as more reliable
function leeKeslerK(comp, T_K, P_bar) {
  const Tr = T_K / comp.Tc;
  if (Tr >= 1.0) {
    // Supercritical component — use Wilson K (Henry's law region)
    return wilsonK(comp, T_K, P_bar);
  }
  const Psat = leeKeslerPsat(comp, T_K);
  return Math.max(1e-10, Math.min(1e8, Psat / P_bar));
}

export function mixtureFugacityCoefficients(T_K, P_bar, z, phase) {
  const n = z.length, R_val = R.BAR_L_MOL_K, RT = R_val * T_K;
  const a = new Float64Array(n), b = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const comp = COMP_LIST[i], Tr = T_K / comp.Tc;
    let k = comp.omega <= 0.491
      ? 0.37464 + 1.54226 * comp.omega - 0.26992 * comp.omega ** 2
      : 0.379642 + 1.48503 * comp.omega - 0.164423 * comp.omega ** 2 + 0.016666 * comp.omega ** 3;
    // Guard alpha: for Tr > 1 (supercritical), alpha can still be evaluated but kappa*(1-sqrt(Tr)) is negative
    const sqrtTr = Math.sqrt(Math.max(0, Tr));
    a[i] = 0.45724 * R_val ** 2 * comp.Tc ** 2 / comp.Pc * Math.max(0, (1 + k * (1 - sqrtTr)) ** 2);
    b[i] = 0.07780 * R_val * comp.Tc / comp.Pc;
  }
  let a_mix = 0, b_mix = 0;
  const ai_mix = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    b_mix += z[i] * b[i];
    for (let j = 0; j < n; j++) {
      const aij = Math.sqrt(Math.max(0, a[i] * a[j])) * (1 - getKij(i, j, T_K));
      a_mix += z[i] * z[j] * aij;
      ai_mix[i] += z[j] * aij;
    }
  }
  const A = a_mix * P_bar / (RT ** 2);
  const B = b_mix * P_bar / RT;
  const roots = solveCubic(1, -(1 - B), A - 3 * B ** 2 - 2 * B, -(A * B - B ** 2 - B ** 3))
    .filter(r => r > B + 1e-8 && isFinite(r));
  if (roots.length === 0) return null;
  const Z = (phase === 'V') ? roots[roots.length - 1] : roots[0];
  if (!isFinite(Z)) return null;

  const phi = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    // Guard all log arguments
    const arg1 = Z - B;
    const arg2_num = Z + 2.4142 * B;
    const arg2_den = Z - 0.4142 * B;
    if (arg1 <= 1e-12 || arg2_num <= 1e-12 || arg2_den <= 1e-12 || arg2_num <= arg2_den) {
      return null; // Can't compute — signal caller to use fallback
    }
    const lnPhi = (b[i] / b_mix) * (Z - 1)
      - Math.log(arg1)
      - (A / (2.8284 * B)) * (2 * ai_mix[i] / a_mix - b[i] / b_mix) * Math.log(arg2_num / arg2_den);
    if (!isFinite(lnPhi)) return null;
    phi[i] = Math.exp(Math.max(-50, Math.min(50, lnPhi)));
    if (!isFinite(phi[i])) return null;
  }
  return phi;
}

// ─── Flash ───────────────────────────────────────────────────────────────────
function wilsonK(c, T_K, P_bar) {
  return (c.Pc / P_bar) * Math.exp(5.373 * (1 + c.omega) * (1 - c.Tc / T_K));
}

export function flashPT(z, T_K, P_bar) {
  // Seed K-values: start with Lee-Kesler (reliable, no cubic) then refine with PR78
  let K = COMP_LIST.map(c => leeKeslerK(c, T_K, P_bar));

  // Validate seed K-values — all must be finite and positive
  if (K.some(k => !isFinite(k) || k <= 0)) {
    K = COMP_LIST.map(c => wilsonK(c, T_K, P_bar));
  }

  let beta = 0.5;
  let x = new Float64Array(N_COMP), y = new Float64Array(N_COMP);

  for (let outer = 0; outer < 50; outer++) {
    const sumKz  = K.reduce((s, ki, i) => s + ki * z[i], 0);
    const sumzK  = K.reduce((s, ki, i) => s + z[i] / ki, 0);

    if (sumKz <= 1.0) {
      // All liquid: beta = 0
      beta = 0; x.set(z);
      for (let i = 0; i < N_COMP; i++) y[i] = Math.min(1, K[i] * z[i]);
      break;
    }
    if (sumzK <= 1.0) {
      // All vapour: beta = 1
      beta = 1; y.set(z);
      for (let i = 0; i < N_COMP; i++) x[i] = Math.min(1, z[i] / Math.max(K[i], 1e-12));
      break;
    }

    // Rachford-Rice bisection for beta
    let lo = 0, hi = 1;
    for (let i = 0; i < 60; i++) {
      beta = (lo + hi) / 2;
      let f = 0;
      for (let j = 0; j < N_COMP; j++) {
        const denom = 1 + beta * (K[j] - 1);
        f += Math.abs(denom) > 1e-12 ? z[j] * (K[j] - 1) / denom : 0;
      }
      if (f > 0) lo = beta; else hi = beta;
    }
    beta = Math.max(0, Math.min(1, beta));

    for (let i = 0; i < N_COMP; i++) {
      const d = 1 + beta * (K[i] - 1);
      x[i] = Math.max(0, z[i] / Math.max(d, 1e-12));
      y[i] = Math.max(0, K[i] * x[i]);
    }
    // Normalise
    let sx = 0, sy = 0;
    for (let i = 0; i < N_COMP; i++) { sx += x[i]; sy += y[i]; }
    if (sx > 0) for (let i = 0; i < N_COMP; i++) x[i] /= sx;
    if (sy > 0) for (let i = 0; i < N_COMP; i++) y[i] /= sy;

    // Try PR78 fugacity update; fall back to Lee-Kesler if PR78 produces bad values
    const pL = mixtureFugacityCoefficients(T_K, P_bar, x, 'L');
    const pV = mixtureFugacityCoefficients(T_K, P_bar, y, 'V');

    if (!pL || !pV) {
      // PR78 failed — stay with current K-values (Lee-Kesler or Wilson)
      // Don't update K: converge on the last good set
      break;
    }

    let maxDK = 0;
    let anyBad = false;
    const K_new = new Float64Array(N_COMP);
    for (let i = 0; i < N_COMP; i++) {
      if (!isFinite(pL[i]) || !isFinite(pV[i]) || pV[i] < 1e-15) { anyBad = true; break; }
      K_new[i] = Math.max(1e-12, Math.min(1e12, pL[i] / pV[i]));
      maxDK = Math.max(maxDK, Math.abs(K_new[i] - K[i]) / Math.max(K[i], 1e-12));
    }
    if (anyBad) break; // PR78 produced bad phi — keep current K and stop

    for (let i = 0; i < N_COMP; i++) K[i] = K_new[i];
    if (outer > 3 && maxDK < 1e-7) break;
  }

  return { beta, x, y };
}

// ─── Enthalpy ────────────────────────────────────────────────────────────────
function mixtureEnthalpyTotal_Jmol(T_K, P_bar, z, phaseHint = 'mix') {
  const T0 = 273.15;
  let Hig = 0;
  for (let i = 0; i < N_COMP; i++) {
    const [A, B, C, D] = COMP_LIST[i].cp;
    Hig += z[i] * (A * (T_K - T0) + B / 2 * (T_K ** 2 - T0 ** 2) + C / 3 * (T_K ** 3 - T0 ** 3) + D / 4 * (T_K ** 4 - T0 ** 4));
  }
  let Hres;
  if (phaseHint === 'V') {
    Hres = penelouxDeparture(T_K, P_bar, z, COMP_LIST, R.BAR_L_MOL_K, 'V');
  } else if (phaseHint === 'L') {
    Hres = penelouxDeparture(T_K, P_bar, z, COMP_LIST, R.BAR_L_MOL_K, 'L');
  } else {
    const f = flashPT(z, T_K, P_bar);
    if (f.beta <= 0) {
      Hres = penelouxDeparture(T_K, P_bar, z, COMP_LIST, R.BAR_L_MOL_K, 'L');
    } else if (f.beta >= 1) {
      Hres = penelouxDeparture(T_K, P_bar, z, COMP_LIST, R.BAR_L_MOL_K, 'V');
    } else {
      const Hres_V = penelouxDeparture(T_K, P_bar, f.y, COMP_LIST, R.BAR_L_MOL_K, 'V');
      const Hres_L = penelouxDeparture(T_K, P_bar, f.x, COMP_LIST, R.BAR_L_MOL_K, 'L');
      Hres = f.beta * (isFinite(Hres_V) ? Hres_V : 0) + (1 - f.beta) * (isFinite(Hres_L) ? Hres_L : 0);
    }
  }
  const H = Hig + (isFinite(Hres) ? Hres : 0);
  return isFinite(H) ? H : Hig; // fallback to ideal gas if departure is bad
}

function findTemperatureFromEnthalpy(targetH, P, z) {
  if (!isFinite(targetH)) return 298.15; // guard against NaN/Inf input
  // Wide range: 150K (-123°C) to 1200K. Use 'mix' phase hint for robustness.
  const T_MIN = 150, T_MAX = 1200;
  const Hlo = mixtureEnthalpyTotal_Jmol(T_MIN, P, z);
  const Hhi = mixtureEnthalpyTotal_Jmol(T_MAX, P, z);
  if (!isFinite(Hlo) || !isFinite(Hhi)) return 298.15;
  if (targetH <= Hlo) return T_MIN;
  if (targetH >= Hhi) return T_MAX;
  // Bisect — H(T) is monotonically increasing for single-phase; may have kink at phase boundary
  let lo = T_MIN, hi = T_MAX;
  for (let i = 0; i < 70; i++) {
    const Tmid = (lo + hi) / 2;
    const Hmid = mixtureEnthalpyTotal_Jmol(Tmid, P, z);
    if (!isFinite(Hmid) || Hmid < targetH) lo = Tmid; else hi = Tmid;
  }
  return (lo + hi) / 2;
}

// ─── Unit Ops ────────────────────────────────────────────────────────────────
export function mixStreams(streams, outId) {
  // Accumulate total molar flow, mole-weighted composition, and mole-weighted enthalpy.
  // All enthalpy on the same basis: mixtureEnthalpyTotal_Jmol (ideal gas + Peneloux residual).
  // H_total [J·kmol/(mol·h)] = sum over streams of F_kmols * H_Jmol
  // H_mix per mol = H_total / F_total  → passed to findTemperatureFromEnthalpy
  let F = 0, H = 0, P = Infinity;
  const zm = new Float64Array(N_COMP);
  for (const s of streams) {
    if (!s.valid || s.F_kmols === 0) continue;
    // Use the stream's known phase hint for the departure calculation
    const phaseHint = s.phase === 'L' ? 'L' : s.phase === 'V' ? 'V' : 'mix';
    const H_s = mixtureEnthalpyTotal_Jmol(s.T_K, s.P_bar, s.z, phaseHint); // J/mol
    F += s.F_kmols;
    H += s.F_kmols * H_s;  // J·kmol/(mol·h)
    P = Math.min(P, s.P_bar);
    for (let i = 0; i < N_COMP; i++) zm[i] += s.F_kmols * s.z[i];
  }
  if (F === 0) return emptyStream(outId);
  for (let i = 0; i < N_COMP; i++) zm[i] /= F;
  const H_mix = H / F;  // J/mol — same basis as mixtureEnthalpyTotal_Jmol
  const T = findTemperatureFromEnthalpy(H_mix, P, zm);
  const f = flashPT(zm, T, P);
  return {
    id: outId, T_K: T, P_bar: P, F_kmols: F, z: zm,
    phase: f.beta > 0.999 ? 'V' : f.beta < 0.001 ? 'L' : 'VL',
    valid: true,
  };
}

export function separatorFlash(inStream, vapId, liqId) {
  // MASS BALANCE: F_vap + F_liq = F_feed exactly
  const f = flashPT(Array.from(inStream.z), inStream.T_K, inStream.P_bar);
  const beta = Math.max(0, Math.min(1, isFinite(f.beta) ? f.beta : 0));
  const F_vap = inStream.F_kmols * beta;
  const F_liq = inStream.F_kmols - F_vap; // ensures exact sum = F_feed
  return {
    vapour: { id: vapId, T_K: inStream.T_K, P_bar: inStream.P_bar, F_kmols: F_vap, z: f.y, phase: 'V', valid: true },
    liquid: { id: liqId, T_K: inStream.T_K, P_bar: inStream.P_bar, F_kmols: F_liq, z: f.x, phase: 'L', valid: true },
    beta
  };
}

export function heatExchanger(hotIn, coldIn, UA, hotOutId, coldOutId) {
  // MASS BALANCE: hotOut.F = hotIn.F and coldOut.F = coldIn.F — always, unconditionally.
  // A heat exchanger only transfers energy between streams; it cannot create or destroy mass.
  // All logic below only changes temperature (and derived phase); F_kmols and z are never touched.

  const Hh = mixtureEnthalpyTotal_Jmol(hotIn.T_K, hotIn.P_bar, hotIn.z);
  const Hc = mixtureEnthalpyTotal_Jmol(coldIn.T_K, coldIn.P_bar, coldIn.z);

  // Build outlet streams first — mass balance is satisfied from the start
  const ho = cloneStream(hotIn,  hotOutId);   // F_kmols = hotIn.F_kmols  ✓
  const co = cloneStream(coldIn, coldOutId);  // F_kmols = coldIn.F_kmols ✓

  const dT_driving = hotIn.T_K - coldIn.T_K;

  // If inverted (cold hotter than hot) or either stream is empty, return unchanged
  if (dT_driving <= 0 || hotIn.F_kmols < 1e-9 || coldIn.F_kmols < 1e-9) {
    return { hotOut: ho, coldOut: co, duty_kW: 0, effectiveness: 0 };
  }

  // Cp = dH/dT  [J/mol/K];  C = Cp * F [kmol/h] / 3.6  [kW/K]
  const Hh1 = mixtureEnthalpyTotal_Jmol(hotIn.T_K  + 1, hotIn.P_bar,  hotIn.z);
  const Hc1 = mixtureEnthalpyTotal_Jmol(coldIn.T_K + 1, coldIn.P_bar, coldIn.z);
  const dHh = (isFinite(Hh1) && isFinite(Hh)) ? Math.max(Math.abs(Hh1 - Hh), 0.1) : 30;
  const dHc = (isFinite(Hc1) && isFinite(Hc)) ? Math.max(Math.abs(Hc1 - Hc), 0.1) : 30;
  const Ch = dHh * hotIn.F_kmols  / 3.6;
  const Cc = dHc * coldIn.F_kmols / 3.6;

  const Cmin = Math.min(Ch, Cc);
  const Cr   = Cmin / Math.max(Ch, Cc, 1e-9);
  const NTU  = UA   / Math.max(Cmin, 1e-9);
  const eff  = Math.abs(Cr - 1) < 1e-4
    ? NTU / (NTU + 1)
    : (1 - Math.exp(-NTU * (1 - Cr))) / (1 - Cr * Math.exp(-NTU * (1 - Cr)));

  const Q = Math.max(0, Math.min(eff * Cmin * dT_driving, Cmin * dT_driving));

  // Energy balance: H_out = H_in ± Q/F  (same basis as mixtureEnthalpyTotal_Jmol)
  const dH_hot  = Q * 3600 / hotIn.F_kmols;   // J/mol removed from hot stream
  const dH_cold = Q * 3600 / coldIn.F_kmols;  // J/mol added to cold stream

  const T_ho = findTemperatureFromEnthalpy(Hh - dH_hot,  hotIn.P_bar,  hotIn.z);
  const T_co = findTemperatureFromEnthalpy(Hc + dH_cold, coldIn.P_bar, coldIn.z);

  // Apply temperatures — enforce 2nd law (no temperature crossover)
  // In counterflow: hot_out > cold_in, cold_out < hot_in
  ho.T_K = Math.max(coldIn.T_K + 0.1, isFinite(T_ho) ? T_ho : hotIn.T_K);
  co.T_K = Math.min(hotIn.T_K  - 0.1, isFinite(T_co) ? T_co : coldIn.T_K);

  // Compute LMTD for display (counterflow)
  const dT1 = hotIn.T_K  - co.T_K;   // hot inlet vs cold outlet
  const dT2 = ho.T_K     - coldIn.T_K; // hot outlet vs cold inlet
  let LMTD_K = 0;
  if (dT1 > 0 && dT2 > 0) {
    LMTD_K = Math.abs(dT1 - dT2) < 0.01 ? (dT1 + dT2) / 2 : (dT1 - dT2) / Math.log(dT1 / dT2);
  }

  // Determine phases safely
  try { ho.phase = flashPT(Array.from(ho.z), ho.T_K, ho.P_bar).beta > 0.999 ? 'V' : flashPT(Array.from(ho.z), ho.T_K, ho.P_bar).beta < 0.001 ? 'L' : 'VL'; }
  catch (_) { ho.phase = hotIn.phase; }
  try { co.phase = flashPT(Array.from(co.z), co.T_K, co.P_bar).beta > 0.999 ? 'V' : flashPT(Array.from(co.z), co.T_K, co.P_bar).beta < 0.001 ? 'L' : 'VL'; }
  catch (_) { co.phase = coldIn.phase; }

  return { hotOut: ho, coldOut: co, duty_kW: Q, effectiveness: eff, LMTD_K };
}

export function jtValve(inStream, P2, outId) {
  // MASS BALANCE: F_out = F_in exactly. JT is isenthalpic — only T and phase change.
  // Physical constraint: dropping pressure on a liquid near its bubble point ALWAYS
  // causes cooling and/or flashing. Temperature cannot increase for NGL streams.
  //
  // Method: compute inlet enthalpy (liquid departure at inlet P), then find T2 at P2
  // such that the two-phase enthalpy equals inlet enthalpy.
  // H_in = H_liq(T1, P1)
  // H_out(T2) = (1-β)×H_liq(T2,P2) + β×H_vap(T2,P2)  [if two-phase]
  //           = H_liq(T2, P2)                            [if still liquid]
  // Solve: H_out(T2) = H_in, with physical bound T2 ≤ T1 for P2 < P1

  const T1 = inStream.T_K;
  const P1 = inStream.P_bar;
  const z  = inStream.z;

  // Compute inlet enthalpy using correct phase
  const f_in = flashPT(Array.from(z), T1, P1);
  let H_in;
  if (f_in.beta <= 0.001) {
    // Liquid inlet
    H_in = mixtureEnthalpyTotal_Jmol(T1, P1, z, 'L');
  } else if (f_in.beta >= 0.999) {
    H_in = mixtureEnthalpyTotal_Jmol(T1, P1, z, 'V');
  } else {
    const HL = mixtureEnthalpyTotal_Jmol(T1, P1, f_in.x, 'L');
    const HV = mixtureEnthalpyTotal_Jmol(T1, P1, f_in.y, 'V');
    H_in = (1 - f_in.beta) * (isFinite(HL) ? HL : 0)
         + f_in.beta        * (isFinite(HV) ? HV : 0);
  }
  if (!isFinite(H_in)) H_in = mixtureEnthalpyTotal_Jmol(T1, P1, z, 'L');

  // Bisect for T2 at P2 — enthalpy is correctly computed including latent heat
  // Physical bound: T2 ≤ T1 when P2 < P1 (JT always cools or stays same for NGLs)
  const T_max = P2 < P1 ? T1 : T1 + 50; // cannot warm on pressure drop
  const T_min = Math.max(100, T1 - 200); // max 200K cooling

  const evalH_out = (T) => {
    const f = flashPT(Array.from(z), T, P2);
    if (f.beta <= 0.001) return mixtureEnthalpyTotal_Jmol(T, P2, z, 'L');
    if (f.beta >= 0.999) return mixtureEnthalpyTotal_Jmol(T, P2, z, 'V');
    const HL = mixtureEnthalpyTotal_Jmol(T, P2, f.x, 'L');
    const HV = mixtureEnthalpyTotal_Jmol(T, P2, f.y, 'V');
    return (1 - f.beta) * (isFinite(HL) ? HL : 0)
         + f.beta        * (isFinite(HV) ? HV : 0);
  };

  const H_lo = evalH_out(T_min);
  const H_hi = evalH_out(T_max);

  let T2 = T_max; // default: no cooling (fallback)
  if (isFinite(H_lo) && isFinite(H_hi)) {
    if (H_in <= H_lo) {
      T2 = T_min; // inlet colder than minimum — clamp
    } else if (H_in >= H_hi) {
      T2 = T_max; // inlet hotter than max — no phase change, minimal cooling
    } else {
      let lo = T_min, hi = T_max;
      for (let i = 0; i < 70; i++) {
        const Tmid = (lo + hi) / 2;
        const Hmid = evalH_out(Tmid);
        if (!isFinite(Hmid) || Hmid < H_in) lo = Tmid; else hi = Tmid;
      }
      T2 = (lo + hi) / 2;
    }
  }

  const z_out = new Float64Array(z); // composition unchanged for JT (no separation)

  // Final flash at T2, P2 — use enthalpy lever rule for vapour fraction
  // This avoids the K≈1 saturation instability in Rachford-Rice:
  // When T2 ≈ saturation temperature, K≈1 and sumKz can be 0.999 or 1.001
  // causing R-R to return all-liquid or all-vapour incorrectly.
  // Instead: beta = (H_in - H_sat_liq) / (H_sat_vap - H_sat_liq)
  let beta2 = 0;
  const H_liq2 = mixtureEnthalpyTotal_Jmol(T2, P2, z, 'L');
  const H_vap2 = mixtureEnthalpyTotal_Jmol(T2, P2, z, 'V');
  const dH_latent = H_vap2 - H_liq2;
  if (isFinite(dH_latent) && dH_latent > 10) {
    // Two-phase region: use lever rule
    beta2 = Math.max(0, Math.min(1, (H_in - H_liq2) / dH_latent));
  } else {
    // Outside two-phase region: use standard flash
    try { beta2 = Math.max(0, Math.min(1, flashPT(Array.from(z), T2, P2).beta)); } catch (_) {}
  }

  const out = cloneStream(inStream, outId);
  out.T_K = T2;
  out.P_bar = P2;
  out.z = z_out;
  out.phase = beta2 > 0.999 ? 'V' : beta2 < 0.001 ? 'L' : 'VL';

  return {
    stream: out,
    dT_K: T2 - T1,
    T_out_C: T2 - 273.15,
    vapourFraction: beta2,
  };
}

export function centrifugalCompressor(inS, P2, eta, outId) {
  const { Z } = mixtureZ(inS.T_K, inS.P_bar, inS.z);
  const Cp = mixtureCp_J_molK(inS.T_K, inS.z);
  const n = (Cp / (Cp - R.J_MOL_K * Z)) / (eta - (eta - 1) * (Cp / (Cp - R.J_MOL_K * Z)));
  const T2 = inS.T_K * (P2 / inS.P_bar) ** ((n - 1) / n);
  const pow = inS.F_kmols * (Cp / 18) * (T2 - inS.T_K) / 3600; // estimated MW
  const out = cloneStream(inS, outId); out.T_K = T2; out.P_bar = P2;
  return { stream: out, power_kW: pow, T2_C: T2 - 273.15 };
}

export function pump(inStream, P2_bar, efficiency, outId) {
  const out = cloneStream(inStream, outId);
  out.P_bar = P2_bar;
  // Liquid is nearly incompressible, T rise is small
  const work_kJ_kmol = (P2_bar - inStream.P_bar) * 100 * 0.05 / efficiency; // 0.05 L/mol approx V
  const dT = work_kJ_kmol / 75; // approx Cp_liq = 75 J/mol-K
  out.T_K = inStream.T_K + dT;
  const power_kW = inStream.F_kmols * work_kJ_kmol / 3600;
  out.phase = 'L';
  return { stream: out, power_kW, T_out_C: out.T_K - 273.15 };
}

export function firedHeater(inS, T2, outId) {
  // MASS BALANCE: F_out = F_in. Only temperature changes.
  const H1 = mixtureEnthalpyTotal_Jmol(inS.T_K, inS.P_bar, inS.z);
  const H2 = mixtureEnthalpyTotal_Jmol(T2,      inS.P_bar, inS.z);
  const dH = isFinite(H2) && isFinite(H1) ? (H2 - H1) : 0;
  const out = cloneStream(inS, outId);
  out.T_K = T2; // F_kmols unchanged by cloneStream ✓
  return { stream: out, duty_kW: inS.F_kmols * dH / 3600 };
}

export function chiller(inS, T2, outId) {
  // MASS BALANCE: F_out = F_in. Only temperature and phase change.
  const H1 = mixtureEnthalpyTotal_Jmol(inS.T_K, inS.P_bar, inS.z);
  const H2 = mixtureEnthalpyTotal_Jmol(T2,      inS.P_bar, inS.z);
  const dH = isFinite(H2) && isFinite(H1) ? (H2 - H1) : 0;
  let beta = 0;
  try { beta = flashPT(inS.z, T2, inS.P_bar).beta; } catch (_) { beta = 0; }
  const out = cloneStream(inS, outId);
  out.T_K = T2; out.phase = beta > 0.999 ? 'V' : 'VL';
  return { stream: out, duty_kW: Math.abs(inS.F_kmols * dH / 3600), COP: 3.0, compPower_kW: Math.abs(inS.F_kmols * dH / 3600) / 3, vapourFraction: beta };
}

export function reboiler(inS, vapF, outV, outL) {
  let lo = inS.T_K, hi = inS.T_K + 150, T = lo + 20;
  for (let i = 0; i < 40; i++) {
    T = (lo + hi) / 2;
    if (flashPT(inS.z, T, inS.P_bar).beta < vapF) lo = T; else hi = T;
  }
  const f = flashPT(inS.z, T, inS.P_bar);
  const dH = mixtureEnthalpyTotal_Jmol(T, inS.P_bar, inS.z) - mixtureEnthalpyTotal_Jmol(inS.T_K, inS.P_bar, inS.z);
  return {
    vapOut: { id: outV, T_K: T, P_bar: inS.P_bar, F_kmols: inS.F_kmols * f.beta, z: f.y, phase: 'V', valid: true },
    liqOut: { id: outL, T_K: T, P_bar: inS.P_bar, F_kmols: inS.F_kmols * (1 - f.beta), z: f.x, phase: 'L', valid: true },
    duty_kW: inS.F_kmols * dH / 3600, T_reb_C: T - 273.15
  };
}

export function splitter(inS, fracs, ids) {
  return ids.map((id, k) => {
    const out = cloneStream(inS, id);
    out.F_kmols = inS.F_kmols * (fracs[k] || (1 - fracs.reduce((a, b) => a + b, 0)));
    return out;
  });
}

// ─── Rigorous stage-by-stage absorber (Thomas sweep MESH) ────────────────────
// Tray j=0: TOP    — lean oil enters, lean gas exits (warmest: absorption exotherm heats here)
// Tray j=N-1: BOTTOM — feed gas enters (-20°C), rich oil exits (coldest)
//
// Physical temperature profile: BOTTOM is coldest (cold gas feed), TOP is warmest
// (exothermic absorption releases heat as heavy components condense from vapour into lean oil).
//
// Algorithm: outer loop updates tray T from adiabatic H-balance; inner loop solves
// component M+E using per-component stripping factor S_i = K_i(T,P,x,y) * V_total/L_total.
// Forward sweep top-to-bottom for liquid flows; back-compute vapour from total balance.
export function kremserAbsorber(gIn, sIn, N, oG, oS) {
  const P  = gIn.P_bar;
  const NC = N_COMP;
  const V_feed = gIn.F_kmols;   // gas entering at bottom
  const L_feed = sIn.F_kmols;   // lean oil entering at top
  const T_bot_init = gIn.T_K;   // cold gas sets bottom temperature
  const T_top_init = sIn.T_K;   // lean oil enters at top; will warm from absorption

  // ── Kremser seed: estimate per-component absorption fraction ─────────────
  const Tmid = (T_top_init + T_bot_init) / 2;
  const Kw   = COMP_LIST.map(c => wilsonK(c, Tmid, P));
  const kremPhi = COMP_LIST.map((_, i) => {
    const A = L_feed / (Math.max(Kw[i], 1e-12) * V_feed);
    if (A < 1e-9) return 0;
    if (A > 1e6)  return 1;
    if (Math.abs(A - 1.0) < 1e-6) return N / (N + 1); // L'Hôpital: A=1 → N/(N+1)
    const AN1 = Math.pow(Math.min(A, 1e15), N + 1);
    return (AN1 - A) / (AN1 - 1); // correct sign for A<1 (both numerator and denominator negative)
  });

  // Initial component flow arrays
  // j=0 = TOP (lean oil in, lean gas out): vapour is leanest here, liquid is richest
  // j=N-1 = BOTTOM (feed gas in, rich oil out): vapour is richest, liquid is leanest
  const l = Array.from({ length: N }, () => new Float64Array(NC));
  const v = Array.from({ length: N }, () => new Float64Array(NC));
  for (let i = 0; i < NC; i++) {
    const abs_i = gIn.z[i] * V_feed * kremPhi[i];
    for (let j = 0; j < N; j++) {
      // Fraction of absorption complete by tray j:
      // At j=0 (top): most absorption done — liquid is rich, vapour is lean
      // At j=N-1 (bottom): least absorption done — liquid is still lean, vapour is rich
      const frac_absorbed = (N - j) / N; // 1 at top (j=0), 1/N at bottom (j=N-1)
      l[j][i] = sIn.z[i] * L_feed + abs_i * frac_absorbed;
      v[j][i] = Math.max(0, gIn.z[i] * V_feed - abs_i * frac_absorbed);
    }
  }

  // ── Initial T profile: bottom = cold gas T, top = cold+exotherm estimate ──
  // Estimate exotherm: ΔH_abs ≈ sum of component absorption × heat of condensation
  // Simple estimate: ~15°C rise from bottom to top for typical lean oil absorbers
  const T_exotherm_est = 15; // K rise from bottom to top, refined by H-balance
  const T_tray = Array.from({ length: N }, (_, j) => {
    // j=0 top (warmest), j=N-1 bottom (coldest = gas feed T)
    const frac_from_top = j / Math.max(N - 1, 1); // 0 at top, 1 at bottom
    return T_top_init + T_exotherm_est * (1 - frac_from_top) // lean oil T + exotherm at top
         + (T_bot_init - T_top_init) * frac_from_top;        // gradient toward cold bottom
    // Net: T[0] = T_lean + exotherm, T[N-1] = T_gas
  });
  // Clamp — top cannot be below lean oil inlet, bottom cannot be above gas inlet
  T_tray[N-1] = T_bot_init;
  T_tray[0]   = Math.max(T_top_init, T_top_init + T_exotherm_est * 0.5);

  // ── MESH iteration ─────────────────────────────────────────────────────────
  const MAX_OUTER = 40;
  const TOL_T     = 0.1; // K
  let converged   = false;

  for (let outer = 0; outer < MAX_OUTER && !converged; outer++) {

    // ── Compute PR78 K-values at each tray ───────────────────────────────────
    const K_tray = Array.from({ length: N }, (_, j) => {
      let Lj = 0, Vj = 0;
      for (let i = 0; i < NC; i++) { Lj += l[j][i]; Vj += v[j][i]; }
      Lj = Math.max(Lj, 1e-9); Vj = Math.max(Vj, 1e-9);
      const xj = new Float64Array(NC), yj = new Float64Array(NC);
      for (let i = 0; i < NC; i++) { xj[i] = l[j][i] / Lj; yj[i] = v[j][i] / Vj; }
      // Normalise to guard against NaN compositions
      let sx = 0, sy = 0;
      for (let i = 0; i < NC; i++) { sx += xj[i]; sy += yj[i]; }
      if (sx > 0) for (let i = 0; i < NC; i++) xj[i] /= sx;
      else { xj[0] = 1; } // fallback: pure methane
      if (sy > 0) for (let i = 0; i < NC; i++) yj[i] /= sy;
      else { yj[0] = 1; }
      const phiL = mixtureFugacityCoefficients(T_tray[j], P, xj, 'L');
      const phiV = mixtureFugacityCoefficients(T_tray[j], P, yj, 'V');
      const K = new Float64Array(NC);
      for (let i = 0; i < NC; i++) {
        const K_pr = (phiL && phiV && phiV[i] > 1e-15 && isFinite(phiL[i]) && isFinite(phiV[i]))
          ? phiL[i] / phiV[i]
          : wilsonK(COMP_LIST[i], T_tray[j], P);
        K[i] = Math.max(1e-10, Math.min(1e8, K_pr));
      }
      return K;
    });

    // ── Per-component Thomas forward sweep ───────────────────────────────────
    // Use L/V totals frozen at the START of this outer iteration so S_i = K_i*V/L
    // is consistent across all components. Compute new arrays separately then copy.
    const L_tot_frozen = new Float64Array(N);
    const V_tot_frozen = new Float64Array(N);
    for (let j = 0; j < N; j++) {
      for (let i = 0; i < NC; i++) { L_tot_frozen[j] += l[j][i]; V_tot_frozen[j] += v[j][i]; }
      L_tot_frozen[j] = Math.max(L_tot_frozen[j], 1e-9);
      V_tot_frozen[j] = Math.max(V_tot_frozen[j], 1e-9);
    }

    const l_new = Array.from({ length: N }, () => new Float64Array(NC));
    const v_new = Array.from({ length: N }, () => new Float64Array(NC));

    for (let i = 0; i < NC; i++) {
      for (let j = 0; j < N; j++) {
        const S_i = K_tray[j][i] * V_tot_frozen[j] / L_tot_frozen[j];
        const l_above_i = j === 0     ? sIn.z[i] * L_feed : l[j-1][i];
        const v_below_i = j === N - 1 ? gIn.z[i] * V_feed : v[j+1][i];
        const total_i   = l_above_i + v_below_i;
        l_new[j][i] = Math.max(0, total_i / Math.max(1 + S_i, 1e-12));
        v_new[j][i] = Math.max(0, total_i - l_new[j][i]);
      }
    }

    // Damped update: blend 40% new + 60% old to prevent oscillation/divergence
    const damp = outer < 5 ? 0.3 : 0.5; // more conservative early on
    for (let j = 0; j < N; j++) {
      for (let i = 0; i < NC; i++) {
        const l_val = damp * l_new[j][i] + (1 - damp) * l[j][i];
        const v_val = damp * v_new[j][i] + (1 - damp) * v[j][i];
        l[j][i] = isFinite(l_val) ? Math.max(0, l_val) : l[j][i];
        v[j][i] = isFinite(v_val) ? Math.max(0, v_val) : v[j][i];
      }
    }

    // ── Update tray temperatures from adiabatic enthalpy balance ─────────────
    // Skip first iteration — let composition sweep establish a reasonable profile first.
    // Sweep BOTTOM → TOP so T_tray[j+1] is already updated when used in tray j's balance.
    const T_abs_lo = Math.min(T_bot_init, T_top_init) - 5;
    const T_abs_hi = Math.max(T_bot_init, T_top_init) + 60;

    let maxDeltaT = 0;
    if (outer > 0) {
    for (let j = N - 1; j >= 0; j--) {
      let Lj = 0, Vj = 0;
      for (let i = 0; i < NC; i++) { Lj += l[j][i]; Vj += v[j][i]; }
      if (Lj < 1e-9 || Vj < 1e-9) continue;
      const xj = new Float64Array(NC), yj = new Float64Array(NC);
      for (let i = 0; i < NC; i++) { xj[i] = l[j][i] / Lj; yj[i] = v[j][i] / Vj; }

      // Inlet liquid: from tray above (j-1) or lean oil feed at top
      const T_in_L = j === 0 ? sIn.T_K : T_tray[j - 1];
      let Lj_in = 0;
      const x_in = new Float64Array(NC);
      if (j === 0) {
        Lj_in = L_feed;
        for (let i = 0; i < NC; i++) x_in[i] = sIn.z[i];
      } else {
        for (let i = 0; i < NC; i++) Lj_in += l[j - 1][i];
        for (let i = 0; i < NC; i++) x_in[i] = Lj_in > 1e-9 ? l[j - 1][i] / Lj_in : 0;
      }

      // Inlet vapour: from tray below (j+1) or gas feed at bottom
      const T_in_V = j === N - 1 ? gIn.T_K : T_tray[j + 1];
      let Vj_in = 0;
      const y_in = new Float64Array(NC);
      if (j === N - 1) {
        Vj_in = V_feed;
        for (let i = 0; i < NC; i++) y_in[i] = gIn.z[i];
      } else {
        for (let i = 0; i < NC; i++) Vj_in += v[j + 1][i];
        for (let i = 0; i < NC; i++) y_in[i] = Vj_in > 1e-9 ? v[j + 1][i] / Vj_in : 0;
      }

      // Adiabatic enthalpy balance: H_in = H_out
      // Use 'mix' phase hint — compositions near bubble/dew point; let the function
      // do a flash to pick the right departure rather than forcing L or V.
      const H_in = Lj_in * mixtureEnthalpyTotal_Jmol(T_in_L, P, x_in, 'L')
                 + Vj_in * mixtureEnthalpyTotal_Jmol(T_in_V, P, y_in, 'V');

      const evalH = (T) =>
        Lj * mixtureEnthalpyTotal_Jmol(T, P, xj, 'L') +
        Vj * mixtureEnthalpyTotal_Jmol(T, P, yj, 'V');

      const H_lo = evalH(T_abs_lo), H_hi = evalH(T_abs_hi);
      let T_new = T_tray[j];

      // Guard against non-monotone H(T) (can happen in two-phase region)
      if (!isFinite(H_lo) || !isFinite(H_hi) || !isFinite(H_in)) {
        // Skip this tray update — keep previous temperature
      } else if (H_in <= H_lo) {
        T_new = T_abs_lo;
      } else if (H_in >= H_hi) {
        T_new = T_abs_hi;
      } else {
        let blo = T_abs_lo, bhi = T_abs_hi;
        for (let bi = 0; bi < 45; bi++) {
          T_new = (blo + bhi) / 2;
          const Hmid = evalH(T_new);
          if (!isFinite(Hmid) || Hmid < H_in) blo = T_new; else bhi = T_new;
        }
        T_new = (blo + bhi) / 2;
      }

      maxDeltaT = Math.max(maxDeltaT, Math.abs(T_new - T_tray[j]));
      T_tray[j] = T_tray[j] + 0.5 * (T_new - T_tray[j]);
    }
    } // end if (outer > 0)

    if (maxDeltaT < TOL_T) converged = true;
  }

  // ── Assemble outlets with overall mass balance enforcement ───────────────
  // Use the tray profile to estimate per-component split between gas and liquid outlets.
  // For each component i:
  //   total_in_i = gIn.z[i] * V_feed + sIn.z[i] * L_feed   (kmol/h entering column)
  //   gas_out_i  = component i leaving in lean gas (top vapour)
  //   liq_out_i  = component i leaving in rich oil (bottom liquid)
  //   CONSTRAINT: gas_out_i + liq_out_i = gIn.z[i]*V_feed + sIn.z[i]*L_feed (exact)
  //
  // Strategy: use tray arrays ONLY for composition profiles (mole fractions).
  // Use overall component balances for flows — the Kremser phi gives the correct
  // absorption fraction; the tray solver refines it but may drift due to damping.

  const y_out = new Float64Array(NC);
  const x_out = new Float64Array(NC);
  let Vout = 0, Lout = 0;

  // Refined absorption fraction: blend Kremser seed with tray solver result
  // The tray solver's top vapour composition (normalised) gives the best estimate
  // of which components are in the lean gas. Then apply overall balance.
  let Vtop = 0;
  for (let i = 0; i < NC; i++) Vtop += isFinite(v[0][i]) ? Math.max(0, v[0][i]) : 0;

  for (let i = 0; i < NC; i++) {
    const total_in_i = gIn.z[i] * V_feed + sIn.z[i] * L_feed;

    // Gas-phase fraction from tray solver composition (mole fraction in top vapour)
    // The key insight: we trust the COMPOSITION from the tray solver, but enforce
    // the FLOW from the overall balance using Kremser absorption fractions.
    // Absorption fraction for gas-phase components only (lean oil doesn't evaporate):
    const abs_i = gIn.z[i] * V_feed * kremPhi[i]; // kmol/h absorbed into liquid
    const gas_out_i  = Math.max(0, gIn.z[i] * V_feed - abs_i); // unabsorbed gas
    const liq_out_i  = Math.max(0, sIn.z[i] * L_feed + abs_i); // lean oil + absorbed

    // Sanity check: lean oil components (C7-C9) should not appear in gas out
    // Their Kremser phi ≈ 1 so gas_out_i ≈ 0 automatically for these components

    y_out[i] = gas_out_i;
    x_out[i] = liq_out_i;

    // Final clamp and complement to ensure exact balance
    y_out[i] = Math.max(0, Math.min(total_in_i, y_out[i]));
    x_out[i] = Math.max(0, total_in_i - y_out[i]); // exact complement — no drift possible
    Vout += y_out[i];
    Lout += x_out[i];
  }

  // Overall check (should be exact by construction)
  const F_total_in  = V_feed + L_feed;
  const F_total_out = Vout + Lout;
  if (Math.abs(F_total_out - F_total_in) > 0.01 * F_total_in) {
    // Something went wrong — scale to enforce balance
    const scale = F_total_in / Math.max(F_total_out, 1e-9);
    Vout *= scale; Lout *= scale;
    for (let i = 0; i < NC; i++) { y_out[i] *= scale; x_out[i] *= scale; }
  }

  // Normalise to mole fractions for the stream objects
  const y_frac = new Float64Array(NC);
  const x_frac = new Float64Array(NC);
  if (Vout > 1e-9) for (let i = 0; i < NC; i++) y_frac[i] = y_out[i] / Vout;
  else { for (let i = 0; i < NC; i++) y_frac[i] = gIn.z[i]; Vout = V_feed * 0.01; } // tiny passthrough
  if (Lout > 1e-9) for (let i = 0; i < NC; i++) x_frac[i] = x_out[i] / Lout;
  else { for (let i = 0; i < NC; i++) x_frac[i] = sIn.z[i]; Lout = L_feed; }

  // Outlet temperatures from tray profile
  // Top tray temp = lean gas outlet; bottom tray temp = rich oil outlet
  const T_gas_out  = isFinite(T_tray[0])   ? T_tray[0]   : sIn.T_K;
  const T_rich_out = isFinite(T_tray[N-1]) ? T_tray[N-1] : gIn.T_K;

  // Overall enthalpy balance: Q_abs = H_in - H_out (should be ~0 for adiabatic)
  const H_gasin  = mixtureEnthalpyTotal_Jmol(gIn.T_K,   P, gIn.z,  'V') * V_feed;
  const H_solin  = mixtureEnthalpyTotal_Jmol(sIn.T_K,   P, sIn.z,  'L') * L_feed;
  const H_gasout = mixtureEnthalpyTotal_Jmol(T_gas_out,  P, y_frac, 'V') * Vout;
  const H_solout = mixtureEnthalpyTotal_Jmol(T_rich_out, P, x_frac, 'L') * Lout;
  const Q_abs_kW = isFinite(H_gasin + H_solin - H_gasout - H_solout)
    ? (H_gasin + H_solin - H_gasout - H_solout) / 3600
    : 0;

  return {
    gasOut:     { id: oG, T_K: T_gas_out,  P_bar: P, F_kmols: Vout, z: y_frac, phase: 'V', valid: true },
    solventOut: { id: oS, T_K: T_rich_out, P_bar: P, F_kmols: Lout, z: x_frac, phase: 'L', valid: true },
    Q_abs_kW,
    F_abs_total: V_feed - Vout,
    trayT: T_tray.map(t => t - 273.15),
    converged,
  };
}

// ─── Shared MESH column solver ────────────────────────────────────────────────
// Handles distillation columns (with condenser + reflux) and strippers (reboiler only).
//
// Tray numbering: stage 0 = top (condenser/vapour outlet), stage N-1 = bottom (reboiler).
// Liquid flows down (stage j → j+1), vapour flows up (stage j → j-1).
//
// opts = {
//   hasCondenser: bool,   // true for distillation, false for stripper
//   condT_K: number,      // condenser temperature (distillation only)
//   refluxRatio: number,  // L/D for distillation; V_reb/B for stripper (reboil ratio)
//   feedTray: number,     // 0-indexed tray where feed enters (distillation only)
//   distRate: number,     // distillate flow kmol/h (0 = auto from reflux + enthalpy)
//   P_bar: number,
// }
function meshColumn(fIn, N, opts) {
  const NC = N_COMP;
  const P = opts.P_bar || fIn.P_bar;
  const F = fIn.F_kmols;
  const hasCondenser = opts.hasCondenser !== false;
  const feedTray = Math.max(0, Math.min(N - 1, opts.feedTray || Math.floor(N / 2)));
  const RR = Math.max(0.05, opts.refluxRatio || 1.0); // reflux ratio L/D or reboil ratio V/B

  // ── Initial temperature profile ───────────────────────────────────────────
  const T_feed = fIn.T_K;
  let T_top, T_bot;

  // Reboiler temperature: use setpoint if provided, else estimate bubble point
  if (opts.rebT_K && isFinite(opts.rebT_K)) {
    T_bot = opts.rebT_K;
  } else {
    T_bot = T_feed + 20;
    for (let it = 0; it < 30; it++) {
      const T_cur = T_bot;
      const K  = COMP_LIST.map(c => wilsonK(c, T_cur, P));
      const K2 = COMP_LIST.map(c => wilsonK(c, T_cur + 1, P));
      const f_bub = K.reduce((s, ki, i) => s + ki * fIn.z[i], 0) - 1;
      const dfdT  = K2.reduce((s, ki, i) => s + ki * fIn.z[i], 0) - K.reduce((s, ki, i) => s + ki * fIn.z[i], 0);
      const dT = -f_bub / Math.max(Math.abs(dfdT), 1e-8);
      T_bot += Math.max(-20, Math.min(20, dT));
      if (Math.abs(dT) < 0.2) break;
    }
  }

  // Condenser temperature: use setpoint if provided, else estimate dew point
  if (hasCondenser && opts.condT_K) {
    T_top = opts.condT_K;
  } else {
    T_top = T_feed - 20;
    for (let it = 0; it < 30; it++) {
      const T_cur = T_top;
      const K  = COMP_LIST.map(c => wilsonK(c, T_cur, P));
      const K2 = COMP_LIST.map(c => wilsonK(c, T_cur + 1, P));
      const f_dew = K.reduce((s, ki, i) => s + fIn.z[i] / ki, 0) - 1;
      const dfdT  = K2.reduce((s, ki, i) => s + fIn.z[i] / ki, 0) - K.reduce((s, ki, i) => s + fIn.z[i] / ki, 0);
      const dT = -f_dew / Math.max(Math.abs(dfdT), 1e-8);
      T_top += Math.max(-20, Math.min(20, dT));
      if (Math.abs(dT) < 0.2) break;
    }
  }
  T_top = Math.max(150, T_top);
  T_bot = Math.max(T_top + 5, T_bot);

  // Linear temperature profile
  const T_tray = Array.from({ length: N }, (_, j) => T_top + (T_bot - T_top) * j / Math.max(N - 1, 1));

  // ── Initial component flows: Fenske-based split estimate ─────────────────
  // Use Fenske minimum stages to estimate per-component recovery at actual reflux.
  const T_mid = (T_top + T_bot) / 2;
  const K_cond = COMP_LIST.map(c => leeKeslerK(c, T_top,  P));
  const K_reb  = COMP_LIST.map(c => leeKeslerK(c, T_bot,  P));
  const K_feed = COMP_LIST.map(c => leeKeslerK(c, T_feed, P)); // eslint-disable-line no-unused-vars
  const K_mid_arr = COMP_LIST.map(c => leeKeslerK(c, T_mid, P)); // eslint-disable-line no-unused-vars

  // Geometric mean K across column (Fenske)
  const K_geom = COMP_LIST.map((_, i) => Math.sqrt(K_cond[i] * K_reb[i]));

  // Find the light key (highest K < 10 that has significant feed flow)
  // and heavy key (K < light key, also significant in feed)
  let lkIdx = 1, hkIdx = 2; // default: C2 = LK, C3 = HK
  if (opts.lkIdx !== undefined) lkIdx = opts.lkIdx;
  if (opts.hkIdx !== undefined) hkIdx = opts.hkIdx;

  const alpha_lk = K_geom[lkIdx] / Math.max(K_geom[hkIdx], 1e-10); // relative volatility LK/HK

  // Fenske: Nmin = ln((xLK/xHK)_D / (xLK/xHK)_B) / ln(alpha_lk)
  // At specified recoveries (rec_lk, rec_hk from opts or defaults):
  const rec_lk = opts.rec_lk ?? 0.95; // fraction of LK in distillate
  const rec_hk = opts.rec_hk ?? 0.05; // fraction of HK in distillate

  // Use Fenske-Underwood-Gilliland to estimate per-component split at actual R/Rmin
  // Per-component distillate fraction: phi_i = f(alpha_i, alpha_lk, rec_lk, rec_hk)
  // Simplified: Kremser-style absorption factor per component
  //   A_i = L / (K_i * V) — absorption factor
  //   For components lighter than LK: mostly distillate
  //   For components heavier than HK: mostly bottoms
  //   For LK/HK: use specified recoveries

  // Estimate D and B from LK/HK specifications
  const F_lk = fIn.z[lkIdx] * F;
  const F_hk = fIn.z[hkIdx] * F;
  const D_lk = F_lk * rec_lk;
  const B_lk = F_lk * (1 - rec_lk);
  const D_hk = F_hk * rec_hk;
  const B_hk = F_hk * (1 - rec_hk);

  // Overall D from LK balance: D ≈ (sum of light components + D_lk + D_hk - light_non_key)
  // Use K-based split for each component: phi_i = fraction going to distillate
  const phi = new Float64Array(NC); // fraction of each component going to distillate
  for (let i = 0; i < NC; i++) {
    if (i === lkIdx) { phi[i] = rec_lk; continue; }
    if (i === hkIdx) { phi[i] = rec_hk; continue; }
    // Kremser-style: use relative volatility vs HK
    const alpha_i = K_geom[i] / Math.max(K_geom[hkIdx], 1e-10);
    if (alpha_i > alpha_lk) {
      // Lighter than LK: goes mostly overhead, scaled by how much lighter
      phi[i] = Math.min(0.999, rec_lk * Math.pow(alpha_i / alpha_lk, 1.5));
    } else if (alpha_i < 1.0) {
      // Heavier than HK: goes mostly bottoms
      phi[i] = Math.max(0.001, rec_hk * Math.pow(alpha_i, 1.5));
    } else {
      // Between HK and LK: interpolate
      const t = (alpha_i - 1.0) / Math.max(alpha_lk - 1.0, 0.01);
      phi[i] = rec_hk + t * (rec_lk - rec_hk);
    }
  }

  let D_est = 0, B_est = 0;
  const d0 = new Float64Array(NC), b0 = new Float64Array(NC);
  for (let i = 0; i < NC; i++) {
    d0[i] = Math.max(0, fIn.z[i] * F * phi[i]);
    b0[i] = Math.max(0, fIn.z[i] * F * (1 - phi[i]));
    D_est += d0[i]; B_est += b0[i];
  }
  D_est = Math.max(0.01 * F, Math.min(0.99 * F, D_est));
  B_est = F - D_est;
  // Re-scale d0, b0 to sum to F
  for (let i = 0; i < NC; i++) {
    d0[i] = Math.max(0, fIn.z[i] * F * phi[i]);
    b0[i] = fIn.z[i] * F - d0[i];
  }

  // Actual reflux: L = RR * D, V = (1 + RR) * D
  const L_top = RR * D_est;        // kmol/h liquid at top
  const V_top = (1 + RR) * D_est;  // kmol/h vapour at top
  // Feed condition q: fraction liquid in feed (q=1 for subcooled liquid, q=0 for saturated vapour)
  // Simple estimate from feed flash
  let q_feed = 0.5;
  try {
    const f_flash = flashPT(Array.from(fIn.z), fIn.T_K, P);
    q_feed = Math.max(0, Math.min(1, 1 - f_flash.beta));
  } catch (_) { q_feed = 0.5; }
  // Below feed tray: L_bot = L_top + q_feed * F, V_bot = V_top - (1 - q_feed) * F
  const L_bot = L_top + q_feed * F;
  const V_bot = Math.max(0.01 * F, V_top - (1 - q_feed) * F);

  // l[j][i] = liquid flow of component i leaving tray j (kmol/h)
  // v[j][i] = vapour flow of component i leaving tray j
  const l = Array.from({ length: N }, () => new Float64Array(NC));
  const v = Array.from({ length: N }, () => new Float64Array(NC));

  for (let j = 0; j < N; j++) {
    // L/V profile: linear transition around feed tray
    const isAboveFeed = j <= feedTray;
    const L_j = isAboveFeed ? L_top : L_bot;
    const V_j = isAboveFeed ? V_top : V_bot;
    const K_j = COMP_LIST.map(c => leeKeslerK(c, T_tray[j], P));

    // Distribute components based on absorption factor A_i = L/(K_i * V)
    for (let i = 0; i < NC; i++) {
      const A_i = L_j / Math.max(K_j[i] * V_j, 1e-12);
      // phi at this tray: 1 = all liquid, 0 = all vapour
      const frac_liq = A_i / (1 + A_i); // fraction in liquid at equilibrium
      const total_i = fIn.z[i] * F; // component i total flow
      l[j][i] = Math.max(1e-10, total_i * frac_liq);
      v[j][i] = Math.max(1e-10, total_i * (1 - frac_liq));
    }
  }

  // ── MESH iteration ─────────────────────────────────────────────────────────
  const MAX_OUTER = 120, MAX_INNER = 50;  // more iterations for difficult columns
  const TOL_T = 0.5, TOL_COMP = 1e-4;
  let converged = false, iterations = 0;

  for (let outer = 0; outer < MAX_OUTER && !converged; outer++) {
    iterations = outer + 1;

    // ── Inner solve: Thomas algorithm (tridiagonal) for M-equations ────────────
    // At fixed T-profile and K-values, the MESH M-equations for each component i form
    // an independent tridiagonal system in l[j][i]:
    //   For each tray j: L_{j-1}*x_{j-1,i} - (L_j + K_{j,i}*V_j)*x_{j,i} + K_{j+1,i}*V_{j+1}*x_{j+1,i} = -F_j*z_i
    //   where y_{j,i} = K_{j,i} * x_{j,i}
    //
    // This is exact (no iteration) and stable. L/V profile comes from the outer loop seed.

    // Step 1: compute K-values at current T profile
    const K_tray_inner = [];
    for (let j = 0; j < N; j++) {
      let Lj = 0, Vj = 0;
      for (let i = 0; i < NC; i++) { Lj += l[j][i]; Vj += v[j][i]; }
      Lj = Math.max(Lj, 1e-9); Vj = Math.max(Vj, 1e-9);
      const xj = new Float64Array(NC), yj = new Float64Array(NC);
      for (let i = 0; i < NC; i++) { xj[i] = l[j][i] / Lj; yj[i] = v[j][i] / Vj; }
      const phiL = mixtureFugacityCoefficients(T_tray[j], P, xj, 'L');
      const phiV = mixtureFugacityCoefficients(T_tray[j], P, yj, 'V');
      const K = new Float64Array(NC);
      for (let i = 0; i < NC; i++) {
        K[i] = (phiL && phiV && phiV[i] > 1e-15)
          ? Math.max(1e-10, Math.min(1e10, phiL[i] / phiV[i]))
          : leeKeslerK(COMP_LIST[i], T_tray[j], P);
      }
      K_tray_inner.push(K);
    }

    // Step 2: compute L/V profile from current tray totals
    const L_tray = new Float64Array(N), V_tray = new Float64Array(N);
    for (let j = 0; j < N; j++) {
      for (let i = 0; i < NC; i++) { L_tray[j] += l[j][i]; V_tray[j] += v[j][i]; }
      L_tray[j] = Math.max(L_tray[j], 1e-6);
      V_tray[j] = Math.max(V_tray[j], 1e-6);
    }

    // Step 3: compute boundary flows (condenser reflux, reboiler vapour) per component
    const l_above_bound = new Float64Array(NC); // liquid entering tray 0 from condenser
    const v_below_bound = new Float64Array(NC); // vapour entering tray N-1 from reboiler

    // Condenser reflux
    if (hasCondenser || opts.condT_K) {
      let Vt = 0; for (let i = 0; i < NC; i++) Vt += v[0][i];
      const yTop = new Float64Array(NC);
      for (let i = 0; i < NC; i++) yTop[i] = Vt > 0 ? v[0][i] / Vt : 0;
      const condT = opts.condT_K || T_tray[0];
      const f_cond = flashPT(Array.from(yTop), condT, P);
      const condensable = Math.max(0, 1 - f_cond.beta);
      const refluxRatio_eff = hasCondenser ? RR : Math.min(RR, 2.0);
      const L_reflux = Vt * condensable * refluxRatio_eff / (1 + refluxRatio_eff);
      for (let i = 0; i < NC; i++) l_above_bound[i] = f_cond.x[i] * L_reflux;
    }

    // Reboiler vapour
    {
      let Lb = 0; for (let i = 0; i < NC; i++) Lb += l[N-1][i];
      const xBot = new Float64Array(NC);
      for (let i = 0; i < NC; i++) xBot[i] = Lb > 0 ? l[N-1][i] / Lb : 0;
      const vapFrac = Math.min(RR / (1 + RR), 0.95);
      const f_reb = flashPT(Array.from(xBot), T_tray[N-1], P);
      const effectiveVapFrac = Math.min(vapFrac, Math.max(0, f_reb.beta));
      const V_reb = Lb * effectiveVapFrac;
      for (let i = 0; i < NC; i++) v_below_bound[i] = f_reb.y[i] * V_reb;
    }

    // Step 4: Thomas algorithm per component
    // Tridiagonal system: a[j]*l[j-1] + b[j]*l[j] + c[j]*l[j+1] = d[j]
    // where:
    //   a[j] = -L_tray[j-1]  (liquid from tray above enters current tray)
    //   b[j] = L_tray[j] + K[j]*V_tray[j]  (liquid + vapour leaving current tray)
    //   c[j] = -K[j+1]*V_tray[j+1]  (vapour from tray below returns as equilibrium vapour)
    //   d[j] = feed_i on this tray + boundary terms

    for (let i = 0; i < NC; i++) {
      const a = new Float64Array(N); // subdiagonal
      const b = new Float64Array(N); // main diagonal
      const c = new Float64Array(N); // superdiagonal
      const d = new Float64Array(N); // RHS

      for (let j = 0; j < N; j++) {
        const K_j   = K_tray_inner[j][i];
        const K_jn  = j < N-1 ? K_tray_inner[j+1][i] : 0;

        a[j] = j > 0 ? -L_tray[j-1] : 0;
        b[j] = L_tray[j] + K_j * V_tray[j];
        c[j] = j < N-1 ? -K_jn * V_tray[j+1] : 0;

        // Feed and boundary contributions to RHS
        d[j] = j === feedTray ? fIn.z[i] * F : 0;

        // Top boundary: condenser liquid enters tray 0 as l_above
        if (j === 0) d[j] += l_above_bound[i];

        // Bottom boundary: reboiler vapour enters tray N-1 from below
        if (j === N-1) d[j] += v_below_bound[i];
      }

      // Forward elimination (Thomas)
      const c_prime = new Float64Array(N);
      const d_prime = new Float64Array(N);
      c_prime[0] = c[0] / Math.max(b[0], 1e-30);
      d_prime[0] = d[0] / Math.max(b[0], 1e-30);
      for (let j = 1; j < N; j++) {
        const denom = b[j] - a[j] * c_prime[j-1];
        c_prime[j] = c[j] / Math.max(Math.abs(denom), 1e-30) * Math.sign(denom || 1);
        d_prime[j] = (d[j] - a[j] * d_prime[j-1]) / Math.max(Math.abs(denom), 1e-30) * Math.sign(denom || 1);
      }

      // Back substitution
      const l_new = new Float64Array(N);
      l_new[N-1] = d_prime[N-1];
      for (let j = N-2; j >= 0; j--) {
        l_new[j] = d_prime[j] - c_prime[j] * l_new[j+1];
      }

      // Damped update toward Thomas solution
      // Use stronger damping early (establish profile) and when changes are large (prevent oscillation)
      // This mimics tray liquid holdup — large holdup = slow response = more damping
      const maxChange = l_new.reduce((mx, lnj, j) => Math.max(mx, Math.abs(lnj - l[j][i]) / Math.max(l[j][i], 0.1)), 0);
      const dampInner = maxChange > 2.0 ? 0.15   // very large swing — be very conservative
                      : maxChange > 0.5 ? 0.25   // moderate swing
                      : outer < 5     ? 0.35   // early iterations — establish profile
                      : 0.50;                  // near convergence — move faster
      for (let j = 0; j < N; j++) {
        const l_thomas = Math.max(0, l_new[j]);
        l[j][i] = l[j][i] + dampInner * (l_thomas - l[j][i]);
      }
    }

    // Recompute v from tray totals to enforce mass balance
    for (let j = 0; j < N; j++) {
      // Compute total entering this tray
      const l_in = j === 0 ? l_above_bound : l[j-1];
      const v_in = j === N-1 ? v_below_bound : v[j+1];
      const feed_z = j === feedTray ? fIn.z : null;

      for (let i = 0; i < NC; i++) {
        const total_i = (j === 0 ? l_above_bound[i] : l[j-1][i])
                      + (j === N-1 ? v_below_bound[i] : v[j+1][i])
                      + (j === feedTray ? fIn.z[i] * F : 0);
        v[j][i] = Math.max(0, total_i - l[j][i]);
      }
    }

    // Check convergence — use change in total liquid per tray
    let maxDelta = 0;
    for (let j = 0; j < N; j++) {
      let Lnew = 0; for (let i = 0; i < NC; i++) Lnew += l[j][i];
      if (L_tray[j] > 1e-9) maxDelta = Math.max(maxDelta, Math.abs(Lnew - L_tray[j]) / L_tray[j]);
    }
    // Note: maxDelta from Thomas solve — always "converged" in one pass since it's direct
    // The outer loop is what iterates (T updates)

    // ── Outer loop: update temperatures from tray enthalpy balances ──────────
    // Pin boundary trays to their setpoints regardless of hasCondenser
    if (opts.rebT_K  && isFinite(opts.rebT_K))  T_tray[N-1] = opts.rebT_K;
    if (opts.condT_K && isFinite(opts.condT_K)) T_tray[0]   = opts.condT_K;

    let maxDeltaT = 0;

    for (let j = 0; j < N; j++) {
      // Skip pinned trays — temperature fixed by setpoint
      const isPinnedBottom = j === N-1 && opts.rebT_K  && isFinite(opts.rebT_K);
      const isPinnedTop    = j === 0   && opts.condT_K && isFinite(opts.condT_K);
      if (isPinnedBottom || isPinnedTop) continue;

      let Lj = 0, Vj = 0;
      for (let i = 0; i < NC; i++) { Lj += l[j][i]; Vj += v[j][i]; }
      if (Lj < 1e-9 || Vj < 1e-9) continue;
      const xj = new Float64Array(NC), yj = new Float64Array(NC);
      for (let i = 0; i < NC; i++) { xj[i] = l[j][i] / Lj; yj[i] = v[j][i] / Vj; }

      // Inlet streams for enthalpy balance
      const l_above_H = new Float64Array(NC);
      if (j === 0 && (hasCondenser || opts.condT_K)) {
        let Vt = 0; for (let i = 0; i < NC; i++) Vt += v[0][i];
        const yTop = new Float64Array(NC);
        for (let i = 0; i < NC; i++) yTop[i] = Vt > 0 ? v[0][i] / Vt : 0;
        const condT = opts.condT_K || T_tray[0];
        const f_cond = flashPT(Array.from(yTop), condT, P);
        const condensable = Math.max(0, 1 - f_cond.beta);
        const L_reflux = Vt * condensable * Math.min(RR, 2.0) / (1 + Math.min(RR, 2.0));
        for (let i = 0; i < NC; i++) l_above_H[i] = f_cond.x[i] * L_reflux;
      } else if (j > 0) {
        for (let i = 0; i < NC; i++) l_above_H[i] = l[j-1][i];
      }

      const v_below = new Float64Array(NC);
      if (j === N - 1) {
        let Lb = 0; for (let i = 0; i < NC; i++) Lb += l[N-1][i];
        const xBot = new Float64Array(NC); for (let i = 0; i < NC; i++) xBot[i] = Lb > 0 ? l[N-1][i] / Lb : 0;
        const vapFrac = Math.min(RR / (1 + RR), 0.95);
        const f_reb = flashPT(Array.from(xBot), T_tray[N-1], P);
        const V_reb = Lb * Math.min(vapFrac, Math.max(0, f_reb.beta));
        for (let i = 0; i < NC; i++) v_below[i] = f_reb.y[i] * V_reb;
      } else {
        for (let i = 0; i < NC; i++) v_below[i] = v[j+1][i];
      }

      let La = 0, Vb = 0;
      for (let i = 0; i < NC; i++) { La += l_above_H[i]; Vb += v_below[i]; }
      La = Math.max(La, 1e-9); Vb = Math.max(Vb, 1e-9);

      const xa = new Float64Array(NC); for (let i = 0; i < NC; i++) xa[i] = l_above_H[i] / La;
      const yb = new Float64Array(NC); for (let i = 0; i < NC; i++) yb[i] = v_below[i] / Vb;

      const T_above = j === 0 ? T_tray[0] : T_tray[j-1];
      const T_below = j === N-1 ? T_tray[N-1] : T_tray[j+1];

      const H_La_v = mixtureEnthalpyTotal_Jmol(T_above, P, xa, 'L');
      const H_Vb_v = mixtureEnthalpyTotal_Jmol(T_below, P, yb, 'V');
      const H_Ff_v = j === feedTray ? mixtureEnthalpyTotal_Jmol(fIn.T_K, P, fIn.z, 'mix') : 0;
      if (!isFinite(H_La_v) || !isFinite(H_Vb_v) || !isFinite(H_Ff_v)) continue;
      const H_in_total = La * H_La_v + Vb * H_Vb_v + (j === feedTray ? F * H_Ff_v : 0);
      if (!isFinite(H_in_total)) continue;

      // Bisect for T_tray[j] such that Lj*H_L(T) + Vj*H_V(T) = H_in_total
      const evalH = (T) => {
        const HL = mixtureEnthalpyTotal_Jmol(T, P, xj, 'L');
        const HV = mixtureEnthalpyTotal_Jmol(T, P, yj, 'V');
        if (!isFinite(HL) || !isFinite(HV)) return null;
        return Lj * HL + Vj * HV;
      };

      // Bisection range: span the full column temperature range plus margin
      const T_lo = Math.max(150, Math.min(T_top, T_bot) - 20);
      const T_hi = Math.max(T_top, T_bot) + 100;

      let T_new = T_tray[j];
      const H_lo = evalH(T_lo), H_hi = evalH(T_hi);
      if (H_lo !== null && H_hi !== null && isFinite(H_in_total) &&
          H_in_total >= H_lo && H_in_total <= H_hi) {
        let bisLo = T_lo, bisHi = T_hi;
        for (let bi = 0; bi < 60; bi++) {
          T_new = (bisLo + bisHi) / 2;
          const Hmid = evalH(T_new);
          if (Hmid === null || Hmid < H_in_total) bisLo = T_new; else bisHi = T_new;
        }
        T_new = (bisLo + bisHi) / 2;
      } else if (H_lo !== null && H_hi !== null) {
        T_new = H_in_total < H_lo ? T_lo : T_hi;
      }

      // Monotone profile guard: each tray must be between its neighbours
      // Use a loose constraint to avoid over-constraining multi-feed columns
      if (j > 0)     T_new = Math.max(T_new, T_tray[j-1] - 5);  // can't be more than 5K cooler than above
      if (j < N - 1) T_new = Math.min(T_new, T_tray[j+1] + 5);  // can't be more than 5K hotter than below

      // Damping schedule: faster early to establish profile, slower later to converge
      // outer 0-10: 30% (establish temperature profile quickly)
      // outer 11-30: 20% (settle down)
      // outer 31+:  15% (fine convergence, prevent oscillation)
      const dampT = outer < 10 ? 0.30 : outer < 30 ? 0.20 : 0.15;
      const dT = Math.abs(T_new - T_tray[j]);
      maxDeltaT = Math.max(maxDeltaT, dT);
      T_tray[j] = T_tray[j] + dampT * (T_new - T_tray[j]);
    }

    if (maxDeltaT < TOL_T) converged = true;
  }

  // ── Assemble products with overall mass balance enforcement ──────────────
  // F_feed = F_overhead + F_bottoms exactly — enforce this regardless of solver state.

  // Overhead composition from top tray vapour
  let Vt = 0;
  for (let i = 0; i < NC; i++) Vt += isFinite(v[0][i]) ? Math.max(0, v[0][i]) : 0;
  const y_ovhd = new Float64Array(NC);
  if (Vt > 1e-9) {
    for (let i = 0; i < NC; i++) y_ovhd[i] = isFinite(v[0][i]) ? Math.max(0, v[0][i]) / Vt : 0;
  } else {
    const K_top = COMP_LIST.map(c => wilsonK(c, T_top, P));
    let ysum = 0;
    for (let i = 0; i < NC; i++) { y_ovhd[i] = K_top[i] * fIn.z[i]; ysum += y_ovhd[i]; }
    if (ysum > 0) for (let i = 0; i < NC; i++) y_ovhd[i] /= ysum;
  }
  // D_flow_raw and B_flow_raw set below based on reboiler flash condition

  // Bottoms composition from bottom tray liquid
  const F_in = fIn.F_kmols; // declared here so it's available in all blocks below
  let Lb = 0;
  for (let i = 0; i < NC; i++) Lb += isFinite(l[N-1][i]) ? Math.max(0, l[N-1][i]) : 0;
  const xBot = new Float64Array(NC);
  if (Lb > 1e-9) {
    for (let i = 0; i < NC; i++) xBot[i] = isFinite(l[N-1][i]) ? Math.max(0, l[N-1][i]) / Lb : 0;
  } else {
    for (let i = 0; i < NC; i++) xBot[i] = fIn.z[i];
  }

  // Reboiler flash
  const T_reb = opts.rebT_K && isFinite(opts.rebT_K) ? opts.rebT_K : T_tray[N-1];
  const f_reb_final = flashPT(Array.from(xBot), T_reb, P);

  // If reboiler is above dew point (all vapour, beta≥0.99), the tray array is unreliable.
  // Fall back to overall K-value split using phi_i computed from Fenske.
  // This gives physically meaningful bottoms even when the MESH solver hasn't converged.
  let D_flow_raw, B_flow_raw;

  if (f_reb_final.beta >= 0.98) {
    // Reboiler is essentially all vapour — use Fenske phi split directly
    D_flow_raw = 0; B_flow_raw = 0;
    for (let i = 0; i < NC; i++) {
      D_flow_raw += phi[i]         * fIn.z[i] * F_in;
      B_flow_raw += (1 - phi[i])   * fIn.z[i] * F_in;
    }
    // Recompute xBot from Fenske bottoms composition
    let Bsum = 0;
    for (let i = 0; i < NC; i++) { xBot[i] = (1 - phi[i]) * fIn.z[i] * F_in; Bsum += xBot[i]; }
    if (Bsum > 0) for (let i = 0; i < NC; i++) xBot[i] /= Bsum;
    // Recompute overhead composition
    let Dsum = 0;
    for (let i = 0; i < NC; i++) { y_ovhd[i] = phi[i] * fIn.z[i] * F_in; Dsum += y_ovhd[i]; }
    if (Dsum > 0) for (let i = 0; i < NC; i++) y_ovhd[i] /= Dsum;
    if (!hasCondenser) { /* stripper: D_flow_raw is vapour overhead */ }
  } else {
    const vapFrac_reb = Math.min(RR / (1 + RR), 0.95);
    const effectiveVapFrac = Math.min(vapFrac_reb, Math.max(0, f_reb_final.beta));
    const V_reb_final = Lb * effectiveVapFrac;
    B_flow_raw = Math.max(0, Lb - V_reb_final);
    D_flow_raw = hasCondenser ? Vt / (1 + RR) : Vt;
  }
  // Enforce F_in = F_overhead + F_bottoms
  const F_out_raw = D_flow_raw + B_flow_raw;
  let D_flow, B_flow;
  if (F_out_raw > 1e-9 && isFinite(F_out_raw)) {
    const scale = F_in / F_out_raw;
    D_flow = D_flow_raw * scale;
    B_flow = B_flow_raw * scale;
  } else {
    const frac_ovhd = K_geom.reduce((s, k, i) => s + (k > 1 ? fIn.z[i] : 0), 0);
    D_flow = Math.max(0.01 * F_in, Math.min(0.99 * F_in, frac_ovhd * F_in));
    B_flow = F_in - D_flow;
  }

  const T_ovhd   = opts.condT_K ? opts.condT_K : (hasCondenser ? (opts.condT_K || T_tray[0]) : T_tray[0]);
  const T_bottoms = T_reb;

  // Condenser duty
  let condDuty_kW = 0;
  if (hasCondenser && isFinite(T_tray[0]) && isFinite(T_ovhd)) {
    const H_vap = mixtureEnthalpyTotal_Jmol(T_tray[0], P, y_ovhd, 'V');
    const H_liq = mixtureEnthalpyTotal_Jmol(T_ovhd,    P, y_ovhd, 'L');
    if (isFinite(H_vap) && isFinite(H_liq))
      condDuty_kW = Math.max(0, Vt * (H_vap - H_liq) / 3600);
  }

  // Reboiler duty
  const H_liqBot = mixtureEnthalpyTotal_Jmol(T_bottoms, P, xBot, 'L');
  const H_vapBot_arr = f_reb_final.beta >= 0.98 ? xBot : f_reb_final.y; // use feed z if all vapour
  const H_vapBot = mixtureEnthalpyTotal_Jmol(T_bottoms, P, H_vapBot_arr, 'V');
  const rebDuty_kW = (isFinite(H_liqBot) && isFinite(H_vapBot) && B_flow > 0)
    ? Math.max(0, B_flow * (H_vapBot - H_liqBot) / 3600)
    : 0;

  return {
    overheadFlow: D_flow, overheadZ: y_ovhd, T_overhead: T_ovhd,
    bottomsFlow: B_flow,  bottomsZ: xBot,    T_bottoms,
    condDuty_kW, rebDuty_kW,
    trayT: T_tray.map(t => t - 273.15),
    converged, iterations,
    R: RR,
  };
}

// ─── Wang-Henke Column Solver ─────────────────────────────────────────────────
// Implements the Wang-Henke inside-out algorithm from the uploaded reference:
//   Inner loop: TDMA solves ALL tray component MBs simultaneously as a
//               tridiagonal system — guaranteed stable, no absorption-factor issues.
//   Temperature update: Newton-Raphson bubble-point (exact, converges in ~5 iters)
//   Outer loop: enthalpy balance updates V[j] → L[j] — proper energy consistency.
//
// K-values: Lee-Kesler (not Antoine) — correct for high-pressure gas plant (9-55 bar).
// Tray numbering: j=0=bottom, j=N-1=top (same as uploaded solver).
//
// opts.state persists between calls for composition continuity.
function wangHenkeColumn(fIn, N, opts) {
  const NC = N_COMP;
  const P_bar  = opts.P_bar || fIn.P_bar;
  const P_kPa  = P_bar * 100;          // kPa for enthalpy calcs
  const F      = fIn.F_kmols;
  const hasCondenser = opts.hasCondenser !== false;
  const feedTray = Math.max(0, Math.min(N-1, opts.feedTray ?? Math.floor(N/2)));
  const RR     = Math.max(0.1, opts.refluxRatio || 2.0);
  const rebT_K = opts.rebT_K || 532;
  const condT_K = opts.condT_K || (hasCondenser ? 268 : 363);
  const lkIdx  = opts.lkIdx ?? 1;
  const hkIdx  = opts.hkIdx ?? 2;
  const MAX_OUTER = 60, MAX_INNER = 150;
  const TOL_T = 0.5, TOL_V = 1e-4;

  // ── K-value function using Lee-Kesler (replaces Antoine in reference) ─────────
  function kVals(T_K) {
    return COMP_LIST.map(c => leeKeslerK(c, T_K, P_bar));
  }

  // ── Newton-Raphson bubble-point temperature ────────────────────────────────────
  // Solve sum(K_i * x_i) = 1 for T. Far more stable than bisection.
  function bubblePt(x, T_guess) {
    let T = T_guess || condT_K + (rebT_K - condT_K) * 0.5;
    T = Math.max(150, Math.min(1000, T));
    for (let it = 0; it < 80; it++) {
      const K = kVals(T);
      const sumKx = K.reduce((s, k, i) => s + k * x[i], 0);
      const F_bp = sumKx - 1.0;
      if (Math.abs(F_bp) < 1e-8) break;
      // Analytical dF/dT: d(K_i)/dT from Lee-Kesler
      // For sub-critical: K_i = Psat_i(T)/P, d(K)/dT ≈ K * d(lnPsat)/dT
      // From Pitzer: d(lnPsat)/dT ≈ (Hvap)/(R*T^2) ~ (Tb_ratio * 10) / T^2
      // Simpler: finite difference
      const Kp = kVals(T + 1);
      const dFdT = Kp.reduce((s, k, i) => s + (k - K[i]) * x[i], 0);
      const step = F_bp / (dFdT || 1e-8);
      const T_new = Math.max(150, Math.min(1000, T - step));
      if (Math.abs(T_new - T) < 0.01) { T = T_new; break; }
      T = T_new;
    }
    return T;
  }

  // ── CMO initial L/V profile ────────────────────────────────────────────────────
  // For distillation (hasCondenser): D from Fenske, V_top = (RR+1)*D
  // For stripper (no condenser): vapour rises freely, no reflux.
  //   V is set by the reboil ratio: V_reb ≈ RR/(1+RR) * L_bot
  //   Estimate from feed: V_top ≈ F * overall_vap_fraction_at_feed_T
  //   L_top ≈ 0 (no liquid entering from above the top tray in a stripper)
  const K_mid = kVals((rebT_K + condT_K) / 2);
  const phi = COMP_LIST.map((_, i) => {
    if (i < lkIdx) return 0.999;
    if (i === lkIdx) return opts.rec_lk ?? 0.90;
    if (i === hkIdx) return opts.rec_hk ?? 0.05;
    return K_mid[i] > K_mid[lkIdx] ? 0.999 : 0.001;
  });
  const D_fenske = Math.max(0.01*F, Math.min(0.99*F,
    COMP_LIST.reduce((s, _, i) => s + fIn.z[i] * F * phi[i], 0)
  ));
  const B_est = F - D_fenske;

  // For stripper: D is the vapour leaving the top (no condenser/reflux)
  // Use Fenske estimate for overhead flow even in stripper mode
  const D = hasCondenser ? D_fenske : D_fenske;
  const B = F - D;

  const f_feed = flashPT(Array.from(fIn.z), fIn.T_K, P_bar);
  const q = Math.max(0, Math.min(1, 1 - f_feed.beta)); // liquid fraction of feed

  // V and L profiles
  const V_rect  = hasCondenser ? (RR + 1) * D : D;        // stripper: V_top = D_fenske (all overhead)
  const L_rect  = hasCondenser ? RR * D        : 0;        // stripper: no liquid from above top tray
  const V_strip = Math.max(0.01*F, V_rect - (1 - q) * F);
  const L_strip = L_rect + q * F;

  // ── Initialise or restore persistent state ─────────────────────────────────────
  if (!opts.state || !opts.state.T || opts.state.T.length !== N) {
    // Linear T profile + composition gradient from Fenske
    const T_init = Array.from({ length: N }, (_, j) =>
      rebT_K + (condT_K - rebT_K) * (j / Math.max(N-1, 1))  // j=0=bot, j=N-1=top
    );
    const xD_est = new Float64Array(NC), xB_est = new Float64Array(NC);
    for (let i = 0; i < NC; i++) {
      xD_est[i] = Math.max(1e-8, fIn.z[i] * phi[i] * F / Math.max(D_fenske, 1e-9));
      xB_est[i] = Math.max(1e-8, fIn.z[i] * (1-phi[i]) * F / Math.max(B_est, 1e-9));
    }
    let sd = xD_est.reduce((s,v)=>s+v,0), sb = xB_est.reduce((s,v)=>s+v,0);
    for (let i=0;i<NC;i++){xD_est[i]/=sd; xB_est[i]/=sb;}

    const x_init = Array.from({ length: N }, (_, j) => {
      const f = j / Math.max(N-1, 1); // 0=bot, 1=top
      const arr = new Float64Array(NC);
      let s = 0;
      for (let i=0;i<NC;i++){arr[i]=Math.max(1e-8,xB_est[i]*(1-f)+xD_est[i]*f);s+=arr[i];}
      for (let i=0;i<NC;i++) arr[i]/=s;
      return arr;
    });

    if (!opts.state) opts.state = {};
    opts.state.T = T_init;
    opts.state.x = x_init;
    opts.state.L = Array.from({length:N},(_,j)=>j>=feedTray?L_rect:L_strip);
    opts.state.V = Array.from({length:N},(_,j)=>j>=feedTray?V_rect:V_strip);
  }

  const T = opts.state.T;
  const x = opts.state.x;
  let L = opts.state.L;
  let V = opts.state.V;

  // ── Reboiler solve (partial reboiler = equilibrium stage j=-1) ─────────────────
  function solveReboiler(x0, T0, L0) {
    const V_reb = Math.max(L0 - B, 0.001 * F);
    let x_reb = Array.from(x0);
    let T_reb = T0;
    for (let it = 0; it < 50; it++) {
      const K = kVals(T_reb);
      let sumKx = 0;
      for (let i = 0; i < NC; i++) sumKx += K[i] * x_reb[i];
      sumKx = Math.max(sumKx, 1e-12);
      const x_new = new Array(NC);
      for (let i = 0; i < NC; i++) x_new[i] = Math.max(1e-8, (L0*x0[i]) / (B + V_reb*K[i]/sumKx));
      let s = 0; for (let i = 0; i < NC; i++) s += x_new[i];
      for (let i = 0; i < NC; i++) x_new[i] /= s;
      const T_new = bubblePt(x_new, T_reb);
      let chg = 0;
      for (let i = 0; i < NC; i++) chg = Math.max(chg, Math.abs(x_new[i] - x_reb[i]));
      x_reb = x_new; T_reb = T_new;
      if (chg < 1e-10) break;
    }
    const K_reb = kVals(T_reb);
    let sumKx2 = 0;
    for (let i = 0; i < NC; i++) sumKx2 += K_reb[i] * x_reb[i];
    sumKx2 = Math.max(sumKx2, 1e-12);
    const y_reb = x_reb.map((xi, i) => K_reb[i] * xi / sumKx2);
    return { x_reb, y_reb, T_reb, V_reb: Math.max(L0 - B, 0.001*F) };
  }

  // ── TDMA solve: all tray component MBs simultaneously ─────────────────────────
  // For component i, tray j (j=0=bottom, j=N-1=top):
  //   a[j]*x[i,j-1] + b[j]*x[i,j] + c[j]*x[i,j+1] = d[j]
  // where:
  //   a[j] = -V[j-1]*Keff[j-1]   (vapour from below)
  //   b[j] =  L[j] + V[j]*Keff[j]
  //   c[j] = -L[j+1]              (liquid from above)
  //   d[j] =  F_j * z[i]
  // Boundary j=0: reboiler vapour added to d[0]
  // Boundary j=N-1: reflux = R*D at y[N-1] composition → absorbed into b[N-1]
  function tdmaSolve(y_reb, V_reb) {
    const K_tray = T.map(Tj => kVals(Tj));
    const sumKx  = K_tray.map((Kj,j) => Math.max(Kj.reduce((s,k,i)=>s+k*x[j][i],0),1e-12));

    const x_new = Array.from({length:N},()=>new Float64Array(NC));

    for (let ci = 0; ci < NC-1; ci++) {
      const a = new Float64Array(N);
      const b = new Float64Array(N);
      const c = new Float64Array(N);
      const d = new Float64Array(N);

      for (let j = 0; j < N; j++) {
        const Keff_j   = K_tray[j][ci]   / sumKx[j];
        const Keff_jm1 = j > 0 ? K_tray[j-1][ci] / sumKx[j-1] : 0;

        c[j] = j < N-1 ? -L[j+1] : 0;
        a[j] = j > 0   ? -V[j-1] * Keff_jm1 : 0;
        b[j] = L[j] + V[j] * Keff_j;

        // Top boundary: reflux enters at y[N-1] = Keff[N-1]*x[N-1]
        // → net: -L[N-1]*Keff*x → subtracts from b[N-1]
        if (j === N-1 && hasCondenser) b[j] -= L[N-1] * Keff_j;

        d[j] = j === feedTray ? F * fIn.z[ci] : 0;
        // Bottom boundary: reboiler vapour enters tray 0
        if (j === 0) d[j] += V_reb * y_reb[ci];
      }

      // Thomas algorithm
      const cp = new Float64Array(N), dp = new Float64Array(N);
      cp[0] = c[0] / Math.max(b[0], 1e-30);
      dp[0] = d[0] / Math.max(b[0], 1e-30);
      for (let j=1;j<N;j++){
        const denom = b[j] - a[j]*cp[j-1];
        cp[j] = j<N-1 ? c[j]/Math.max(Math.abs(denom),1e-30)*Math.sign(denom||1) : 0;
        dp[j] = (d[j] - a[j]*dp[j-1]) / Math.max(Math.abs(denom),1e-30) * Math.sign(denom||1);
      }
      // Back substitution
      x_new[N-1][ci] = Math.max(1e-8, dp[N-1]);
      for (let j=N-2;j>=0;j--) x_new[j][ci] = Math.max(1e-8, dp[j] - cp[j]*x_new[j+1][ci]);
    }

    // Last component from summation, then normalise
    for (let j=0;j<N;j++){
      const s = Array.from(x_new[j]).slice(0,NC-1).reduce((a,b)=>a+b,0);
      x_new[j][NC-1] = Math.max(1e-8, 1-s);
      const tot = x_new[j].reduce((a,b)=>a+b,0);
      for (let i=0;i<NC;i++) x_new[j][i]/=tot;
    }
    return x_new;
  }

  // ── Enthalpy helpers (kJ/kmol, ideal mixture) ─────────────────────────────────
  // Uses component data from COMP_LIST cp coefficients + Hv estimate
  const T_REF = 298.15;
  function hLiq(z_arr, T_K) {
    let h = 0;
    for (let i=0;i<NC;i++){
      const [A,B2,C2,D2] = COMP_LIST[i].cp;
      h += z_arr[i]*(A*(T_K-T_REF)+B2/2*(T_K**2-T_REF**2)+C2/3*(T_K**3-T_REF**3)+D2/4*(T_K**4-T_REF**4));
    }
    return h; // J/mol
  }
  function hVap(z_arr, T_K) {
    // h_V = h_L + Hv_mix; Hv estimated from Watson correlation on Tc/Pc
    // Hv_i ≈ R*Tc*4.5*(1-Tr)^0.38 (Riedel)
    let h = hLiq(z_arr, T_K);
    for (let i=0;i<NC;i++){
      const c = COMP_LIST[i];
      const Tr = T_K/c.Tc;
      if (Tr < 1) {
        const Hv = R.J_MOL_K * c.Tc * 4.5 * Math.pow(1-Tr, 0.38); // J/mol
        h += z_arr[i] * Hv;
      }
    }
    return h; // J/mol
  }

  // ── Energy outer loop: update V from enthalpy balance ─────────────────────────
  function energyUpdate(y_reb, V_reb) {
    const hL_j = T.map((Tj,j)  => hLiq(x[j],Tj));
    const hV_j = T.map((Tj,j)  => hVap(x[j],Tj)); // approx y[j] ≈ K*x[j]/sumKx
    const hF   = hLiq(Array.from(fIn.z), fIn.T_K) * (q) + hVap(Array.from(fIn.z),fIn.T_K)*(1-q);
    const hV_reb = hVap(y_reb, T[0]);

    const Vnew = new Float64Array(N);
    Vnew[N-1] = hasCondenser ? (RR+1)*D : V[N-1]; // top tray V fixed by reflux

    for (let j=0;j<N-1;j++){
      const L_up  = L[j+1];
      const V_dn  = j===0 ? V_reb : Vnew[j-1];
      const hV_dn = j===0 ? hV_reb : hV_j[j-1];
      const F_j   = j===feedTray ? F : 0;
      const numer = L_up*hL_j[j+1] + V_dn*hV_dn + F_j*hF - L[j]*hL_j[j];
      const denom = Math.max(Math.abs(hV_j[j]), 1000);
      let Vj = numer/denom;
      if (!isFinite(Vj)||Vj<0.01*Math.max(D,1)) Vj = 0.5*(RR+1)*Math.max(D,F*0.01);
      if (Vj > 10*F) Vj = 10*F;
      Vnew[j] = Vj;
    }

    // Recompute L from total material balance (top-down)
    const Lnew = new Float64Array(N);
    Lnew[N-1] = hasCondenser ? RR*D : 0;  // stripper: no reflux, L at top = 0
    for (let j=N-2;j>=0;j--){
      const V_dn = j===0 ? V_reb : Vnew[j-1];
      Lnew[j] = Lnew[j+1] + V_dn + (j===feedTray?F:0) - Vnew[j];
      if (!isFinite(Lnew[j])||Lnew[j]<0.001) Lnew[j]=0.001;
      if (Lnew[j]>8*F) Lnew[j]=8*F;
    }
    return {Vnew, Lnew};
  }

  // ── Main iteration ─────────────────────────────────────────────────────────────
  let converged = false;
  let { x_reb, y_reb, V_reb } = solveReboiler(Array.from(x[0]), T[0], L[0]);

  for (let outer = 0; outer < MAX_OUTER && !converged; outer++) {

    // Inner: solve TDMA + bubble-point until T converges
    for (let inner = 0; inner < MAX_INNER; inner++) {
      ({ x_reb, y_reb, V_reb } = solveReboiler(Array.from(x[0]), T[0], L[0]));
      const x_new = tdmaSolve(y_reb, V_reb);

      // Update compositions
      for (let j=0;j<N;j++) for (let i=0;i<NC;i++) x[j][i]=x_new[j][i];

      // Bubble-point temperature update (Newton-Raphson)
      let maxDT = 0;
      for (let j=0;j<N;j++){
        const T_bp = j===0 ? rebT_K : j===N-1 ? condT_K : bubblePt(Array.from(x[j]), T[j]);
        const dT = Math.abs(T_bp - T[j]);
        maxDT = Math.max(maxDT, dT);
        T[j] = T_bp;
      }
      // Pin boundary temperatures
      T[0]   = rebT_K;
      T[N-1] = condT_K;
      if (maxDT < TOL_T) break;
    }

    // Outer: update V from enthalpy balance
    const {Vnew, Lnew} = energyUpdate(y_reb, V_reb);
    const dV_max = V.reduce((m,v,j)=>Math.max(m,Math.abs(Vnew[j]-v)/(Math.abs(v)+1)),0);

    // Adaptive damping (from reference: dampen more when ΔV/V is large)
    const dampV = Math.min(0.6, 0.3/Math.max(dV_max, 0.3));
    for (let j=0;j<N;j++){
      V[j] = V[j] + dampV*(Vnew[j]-V[j]);
      L[j] = L[j] + dampV*(Lnew[j]-L[j]);
      if (V[j]<0.001) V[j]=0.001; if (L[j]<0.001) L[j]=0.001;
    }

    if (dV_max < TOL_V) { converged = true; }
  }

  // Save state
  opts.state.L = L;
  opts.state.V = V;

  // ── Assemble products ──────────────────────────────────────────────────────────
  ({ x_reb, y_reb, V_reb } = solveReboiler(Array.from(x[0]), T[0], L[0]));

  // Overhead: top tray vapour → distillate (total condenser)
  const K_top = kVals(T[N-1]);
  const sumKx_top = Math.max(K_top.reduce((s,k,i)=>s+k*x[N-1][i],0),1e-12);
  const y_top = new Float64Array(NC);
  for (let i=0;i<NC;i++) y_top[i] = K_top[i]*x[N-1][i]/sumKx_top;

  // Physical clamp: K<0.05 at condenser → cannot be in overhead
  let yc_sum = 0;
  for (let i=0;i<NC;i++){y_top[i]=K_top[i]>0.05?y_top[i]:0;yc_sum+=y_top[i];}
  if(yc_sum>0) for(let i=0;i<NC;i++) y_top[i]/=yc_sum;
  else for(let i=0;i<NC;i++) y_top[i]=fIn.z[i];

  const D_flow = hasCondenser ? V[N-1]/(1+RR) : V[N-1];
  const B_flow = Math.max(0, F - D_flow);

  // Bottoms composition: reboiler liquid
  const xBot = new Float64Array(NC);
  let xBot_sum = 0;
  for (let i=0;i<NC;i++) { xBot[i] = isFinite(x_reb[i]) ? Math.max(0, x_reb[i]) : 0; xBot_sum += xBot[i]; }
  if (xBot_sum < 1e-9) {
    // Fallback: use feed composition (should never happen but prevents empty stream)
    for (let i=0;i<NC;i++) xBot[i] = fIn.z[i];
  } else {
    for (let i=0;i<NC;i++) xBot[i] /= xBot_sum;
  }

  // Tray temperatures for display
  const trayT = T.map(t => t-273.15); // already in correct order j=0=bot

  const H_liqBot = mixtureEnthalpyTotal_Jmol(rebT_K, P_bar, xBot, 'L');
  const H_vapBot = mixtureEnthalpyTotal_Jmol(rebT_K, P_bar, xBot, 'V');
  const rebDuty_kW = isFinite(H_liqBot)&&isFinite(H_vapBot)&&B_flow>0
    ? Math.max(0, B_flow*(H_vapBot-H_liqBot)/3600) : 0;

  return {
    overheadFlow: D_flow,  overheadZ: y_top,  T_overhead: condT_K,
    bottomsFlow:  B_flow,  bottomsZ:  xBot,   T_bottoms:  rebT_K,
    condDuty_kW: 0, rebDuty_kW,
    trayT,   // j=0=bottom, j=N-1=top (reversed from old meshColumn)
    converged, iterations: MAX_OUTER,
    R: RR,
    _state: opts.state,
  };
}

// Alias: trayHoldupColumn now uses Wang-Henke under the hood
function trayHoldupColumn(fIn, N, opts) { return wangHenkeColumn(fIn, N, opts); }
export { trayHoldupColumn };



export function stripper(rIn, N, reboilRatio, P_bar, rebT_K, lkIdx, hkIdx, condT_K, oV, oL, state) {
  const P = P_bar || rIn.P_bar;
  const feedTray = Math.max(0, Math.min(N-2, Math.floor(N * 0.3)));
  const res = trayHoldupColumn(rIn, N, {
    hasCondenser: false,
    refluxRatio: reboilRatio,
    feedTray,
    P_bar: P,
    rebT_K,
    condT_K: condT_K || 363,
    lkIdx:  lkIdx  ?? 4,
    hkIdx:  hkIdx  ?? 6,
    weirHoldup_kmol: 8,
    state,
  });

  const reboilerDuty_kW = res.rebDuty_kW;
  return {
    vapOut: {
      id: oV, T_K: res.T_overhead, P_bar: P,
      F_kmols: res.overheadFlow, z: res.overheadZ, phase: 'V', valid: true,
    },
    liqOut: {
      id: oL, T_K: res.T_bottoms, P_bar: P,
      F_kmols: res.bottomsFlow, z: res.bottomsZ, phase: 'L', valid: true,
    },
    reboilerDuty_kW,
    trayT: res.trayT,
    converged: res.converged,
    _state: res._state, // return state so caller can persist it
  };
}

export function fugDistillation(fIn, p, oH, bT) {
  const T = fIn.T_K + 20, P = p.P_bar || fIn.P_bar, F = fIn.F_kmols;
  const lk = p.lkIdx, hk = p.hkIdx;
  const d = new Float64Array(N_COMP), b = new Float64Array(N_COMP);
  let D = 0, B = 0;
  for (let i = 0; i < N_COMP; i++) {
    // Components lighter than LK go ~fully overhead
    // LK splits at rec_lk in overhead
    // HK splits at rec_hk in overhead
    // Components heavier than HK go ~fully to bottoms
    let r;
    if (i < lk) r = 0.999;           // lighter than LK: essentially all overhead
    else if (i === lk) r = p.rec_lk;  // light key recovery in overhead
    else if (i === hk) r = p.rec_hk;  // heavy key recovery in overhead
    else if (i > hk) r = 0.001;       // heavier than HK: essentially all bottoms
    else r = p.rec_lk - (p.rec_lk - p.rec_hk) * (i - lk) / (hk - lk); // linear interpolation between keys
    d[i] = fIn.z[i] * F * r; D += d[i];
    b[i] = fIn.z[i] * F * (1 - r); B += b[i];
  }
  for (let i = 0; i < N_COMP; i++) { if (D > 0) d[i] /= D; if (B > 0) b[i] /= B; }

  // Estimate condenser and reboiler duties using latent heat approximation
  const condT_K = (p.condT_C != null ? p.condT_C : -15) + 273.15;
  const rebT_K = T + 40; // approximate reboiler temp
  let dHvap_cond = 0, dHvap_reb = 0;
  for (let i = 0; i < N_COMP; i++) {
    if (d[i] <= 0) continue;
    const c = COMP_LIST[i];
    const Tr = condT_K / c.Tc;
    if (Tr < 1) dHvap_cond += d[i] * R.J_MOL_K * c.Tc * Math.max(0, 3.978 * Math.pow(Tr, 1/3) - 3.938 + 1.555 * Math.log(Tr));
    const Tr2 = rebT_K / c.Tc;
    if (Tr2 < 1) dHvap_reb += b[i] * R.J_MOL_K * c.Tc * Math.max(0, 3.978 * Math.pow(Tr2, 1/3) - 3.938 + 1.555 * Math.log(Tr2));
  }
  const RR = (p.R_mult || 1.3) * 1.0; // approximate reflux ratio
  const condDuty_kW = (1 + RR) * D * dHvap_cond / 3600;
  const rebDuty_kW  = condDuty_kW * 1.05 + fIn.F_kmols * 500 / 3600; // approx

  return {
    overheadStream: { id: oH, T_K: condT_K, P_bar: P, F_kmols: D, z: d, phase: 'V', valid: true },
    bottomsStream: { id: bT, T_K: T + 30, P_bar: P, F_kmols: B, z: b, phase: 'L', valid: true },
    condDuty_kW, rebDuty_kW, R_min: 1, R: RR, N_min: 5, N_actual: p.N_stages || 12
  };
}

export function partialCondenser(vIn, T, P, ref, oV, oL) {
  const f = flashPT(vIn.z, T + 273.15, P || vIn.P_bar);
  const Fv = vIn.F_kmols * f.beta, Fl = vIn.F_kmols * (1 - f.beta) / (1 + ref);
  return {
    vapOut: { id: oV, T_K: T + 273.15, P_bar: P || vIn.P_bar, F_kmols: Fv, z: f.y, phase: 'V', valid: true },
    liqOut: { id: oL, T_K: T + 273.15, P_bar: P || vIn.P_bar, F_kmols: Fl, z: f.x, phase: 'L', valid: true },
    condDuty_kW: 0, refluxFlow_kmolh: vIn.F_kmols * (1 - f.beta) - Fl
  };
}

export function rigorousDistillation(fIn, p, oH, bT) {
  const P = p.P_bar || fIn.P_bar;
  const res = meshColumn(fIn, p.N_stages, {
    hasCondenser: true,
    condT_K: (p.condT_C != null ? p.condT_C : -15) + 273.15,
    refluxRatio: p.R || 2.0,
    feedTray: Math.max(0, Math.min(p.N_stages - 1, (p.feedTray || Math.floor(p.N_stages / 2)) - 1)),
    distRate: p.distRate || 0,
    P_bar: P,
    rebT_K: (p.rebT_C && p.rebT_C > 0) ? p.rebT_C + 273.15 : null,
    lkIdx:  p.lkIdx  ?? 1,
    hkIdx:  p.hkIdx  ?? 2,
    rec_lk: p.rec_lk ?? 0.95,
    rec_hk: p.rec_hk ?? 0.05,
  });
  return {
    overheadStream: {
      id: oH, T_K: res.T_overhead, P_bar: P,
      F_kmols: res.overheadFlow, z: res.overheadZ, phase: 'V', valid: true,
    },
    bottomsStream: {
      id: bT, T_K: res.T_bottoms, P_bar: P,
      F_kmols: res.bottomsFlow, z: res.bottomsZ, phase: 'L', valid: true,
    },
    condDuty_kW: res.condDuty_kW,
    rebDuty_kW:  res.rebDuty_kW,
    trayT: res.trayT,
    converged: res.converged,
    iterations: res.iterations,
    R: res.R,
  };
}
